/* eslint-disable max-lines */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { AccountingSyncStatusFilter } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { BillingErrorResponse } from '~/infra/error-response/BillingErrorResponse';
import { ErrorResponse, ServerError } from '~/infra/error-response/ErrorResponse';
import { InvoiceErrorResponse } from '~/infra/error-response/InvoiceErrorResponse';
import { PaymentMethodErrorResponse } from '~/infra/error-response/PaymentMethodErrorResponse';
import { PayoutErrorResponse } from '~/infra/error-response/PayoutErrorResponse';
import { RelationshipErrorResponse } from '~/infra/error-response/RelationshipErrorResponse';
import { SignatureErrorResponse } from '~/infra/error-response/SignatureErrorResponse';
import { TeamMemberErrorResponse } from '~/infra/error-response/TeamMemberErrorResponse';
import { sendErrReport } from '~/infra/utils';
import { UserInfoModel, MemberModel, invoiceModel, invoiceSummaryModel, BillingControlDataModel, ActivityLogEntryModel, HistoricAgreementStatsModel, PayoutsSummaryModel, ReferralModel, integrationModel, invoiceDraftModel, proposalTemplateModel, relationshipModel, relationshipSummaryModel, serviceModel, serviceTemplateModel, timeModel, LatestOFACSanctionStatusModel, ProposalTemplateSummaryModel, SalesSummaryModel, TimeActivitiesModel, AgreementImportExtractionModel, ChatMessageModel, ConversationModel } from '~/models';
import { AccountingCustomerModel } from '~/models/AccountingCustomerModel';
import { BillingTimelineModel } from '~/models/BillingTimelineModel';
import { BusinessSettingsModel } from '~/models/BusinessSettingsModel';
import { ClientModel } from '~/models/ClientModel';
import { ContactModel } from '~/models/ContactModel';
import { DraftModel } from '~/models/DraftModel';
import { EmailOnBehalfSettingsModel } from '~/models/EmailOnBehalfSettingsModel';
import { FutureInvoiceModel } from '~/models/FutureInvoiceModel';
import { HistoricRevenueModel } from '~/models/HistoricRevenueModel';
import { InvoiceDraftDatesModel } from '~/models/InvoiceDraftDatesModel';
import { InvoicesDigestModel } from '~/models/invoices-digest/InvoicesDigestModel';
import { PayoutModel } from '~/models/PayoutModel';
import { RecipientModel } from '~/models/RecipientModel';
import { amendmentDraftChangeToDTO } from '~/models/relationship';
import { RemainingCreditReportEntryModel } from '~/models/RemainingCreditReportEntryModel';
import { ServiceStatusType } from '~/models/service/model';
import { ServiceInvoiceModel } from '~/models/ServiceInvoiceModel';
import { SharedLinkModel } from '~/models/SharedLinkModel';
import { TeamInviteModel } from '~/models/TeamInvitesModel';
import { fromDateStampObject } from '~/models/time';
import { bulkImportClientsResponseFromDTO } from '~/pages/ImportClients/importClients.utils';
import { EXTERNAL_PAYOUT_INVOICE_HEADERS } from '~/pages/PayoutsDashboard/PayoutsDashboard.utils';
import { getProposalsReportFilterParams } from '~/pages/Reports/ProposalsReport/ProposalsReport.utils';
import { externalSortField, externalSyncFilter } from '~/store/invoice-query-helpers';
import { AccountingItemType } from '~/types/api/accounting-integration';
import { IntegrationResponseStatus } from '~/types/api.model';
import { utils } from '~/utils';
import { buildAgreementsCsvExportParams } from '~/utils/agreements-csv-export-params-builder';
import { singleContactSummary } from '~/utils/contact-identity.utils';
import { serializeDateUtc } from '~/utils/date.utils';
import { createAgentStreamParser } from '~/utils/parseAgentStreamEvent';
import { createAnalystStreamParser } from '~/utils/parseAnalystStreamEvent';
import { buildRelationshipsParamsFilter } from '~/utils/relationships-params-filter-builder';
import { salesTaxConfigFromDTO } from '~/utils/salesTax';
import { APIHost } from './sendRequest';
import { selectors } from './state-selectors';
const EXPECTED_SIGNATURE_INSTANCE_OPEN_ERRORS = [
    ServerError.LegalTermsNotFound,
    ServerError.LegalTermsSignatureNotEnabled,
    ServerError.DocusealUnavailable
];
const EXPECTED_SIGNATURE_INSTANCE_SYNC_ERRORS = [
    ServerError.LegalTermsSignatureRolesDrifted,
    ServerError.LegalTermsSignatureNotEnabled,
    ServerError.DocusealUnavailable,
    ServerError.RecordConflict
];
export const isExpectedApproveRefusal = (err)=>err.hasServerError([
        ServerError.MismatchingRelationshipVersion,
        ServerError.InvalidRelationshipStatus,
        ServerError.UserAlreadySigned,
        ServerError.RelationshipAlreadyApproved,
        ServerError.FundingSourceNotFound,
        ServerError.SignatureFieldsNotCompleted,
        ServerError.CustomerAddressRequired,
        ServerError.ProposalBeingEdited
    ]);
const isIntegrationNotConnected = (err)=>err.hasServerError(ServerError.IntegrationNotConnected);
export const isNotASignerRefusal = (err)=>err.hasServerError(ServerError.NotASignerOnThisDocument);
export const isExpectedSigningSessionRefusal = (err)=>err.hasServerError([
        ServerError.NotASignerOnThisDocument,
        ServerError.SigningClosedForSigner,
        ServerError.RelationshipNotAwaitingApproval,
        ServerError.ProposalBeingEdited,
        ServerError.LegalTermsNotFound,
        ServerError.DocusealUnavailable
    ]);
export const PROBE_TIMEOUT_MS = 5000;
const REFUSED_GESTURE_CODES = new Set([
    'apply_already_consumed',
    'conversation_stale',
    'undo_expired'
]);
// WHY the code and not the status: 409 also carries turn_in_progress, whose only detector is Sentry.
export const isRefusedSellerGesture = (e)=>{
    var _e_body;
    const code = (_e_body = e.body) === null || _e_body === void 0 ? void 0 : _e_body.error;
    return typeof code === 'string' && REFUSED_GESTURE_CODES.has(code);
};
// WHY these delays: sized to outlast prod's /verify→last-bank-leg lag plus the time to the click; data and query in ANCR-6443.
const BANK_VERIFICATION_REPLAY_DELAYS_MS = [
    1000,
    2000,
    3000
];
export const isUndeletableFundingSource = (e)=>e.hasErrorMessageIncluding('used by other approved relationships') || e.hasServerError(ServerError.FundingSourceTiedToOpenAdHocInvoice);
// WHY only these two: the other reauthorization 400s mean an orphaned mandate or a gesture the FE should not have offered.
export const isSettledDebitAuthorization = (e)=>e.hasServerError([
        ServerError.FundingSourceDebitBlocked,
        ServerError.FundingSourceAlreadyAuthorized
    ]);
export const isCustomerNotFound = (e)=>e.hasServerError(ServerError.CustomerNotFound);
export const isHandledBuyerEmailRejection = (e)=>e.hasServerError([
        ServerError.TargetUserNotBuyer,
        ServerError.DuplicateContact
    ]);
// WHY safe to silence: both OAuth buttons keep a detector — loginFailed(method, reason) plus a user-facing error. 429 is NOT covered here and still reports.
export const isExpectedOauthSignInRejection = (e)=>e.hasStatus([
        403
    ]) || e.hasServerError([
        ServerError.EmailIsInvalid,
        ServerError.AccountIsDisabled
    ]);
export function initStoreApi(param) {
    let { getState, getNow, sendRequest, sendStream } = param;
    const mapResponseBody = (res)=>res.data;
    const isExpectedEmailMailboxUnavailable = (err)=>!!selectors.ff.featureFlagsData(getState()).OUTLOOK_MAILBOX_UNAVAILABLE_MODAL && err.hasServerError(ServerError.EmailMailboxUnavailable);
    const toBillingTimeline = (dto)=>{
        try {
            return BillingTimelineModel.fromDTO(dto);
        } catch (err) {
            sendErrReport(err, {
                dto
            });
            throw err;
        }
    };
    const toAmendmentConversation = (body)=>ConversationModel.fromDTO({
            conversationId: body.conversationId,
            draftId: null,
            turn: body.turn,
            messages: body.messages
        });
    const toCreatedConversation = (body)=>({
            conversationId: body.conversationId,
            draftId: body.draftId,
            message: ChatMessageModel.fromDTO(body.message)
        });
    return {
        places: {
            async autocomplete (data, signal) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/places/autocomplete',
                    data,
                    disable403Handling: true,
                    config: signal ? {
                        signal
                    } : undefined
                });
                return res.data;
            },
            async details (data, signal) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/places/details',
                    data,
                    disable403Handling: true,
                    config: signal ? {
                        signal
                    } : undefined
                });
                return res.data;
            }
        },
        referenceFields: {
            async getNames (params) {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/reference-fields/names',
                    params
                });
                return res.data.names;
            },
            async getValueInUse (params) {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/reference-fields/value-in-use',
                    params
                });
                return res.data.inUse;
            }
        },
        pusher: {
            async authorizeChannel (params) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/pusher/auth',
                    data: {
                        socket_id: params.socketId,
                        channel_name: params.channelName
                    }
                });
                return res.data;
            }
        },
        mobileNotification: {
            register (payload) {
                return sendRequest({
                    method: 'post',
                    url: '/mobile-notification/register',
                    apiHost: APIHost.Scout,
                    data: payload,
                    disable403Handling: true,
                    disableSentry: (error)=>error.hasStatus([
                            403,
                            409
                        ])
                }).then(mapResponseBody);
            },
            unregisterDevice (payload) {
                return sendRequest({
                    method: 'post',
                    url: '/mobile-notification/unregister-device',
                    apiHost: APIHost.Scout,
                    data: payload,
                    disableAuth: true,
                    disableSentry: (error)=>error.isRetryableLater()
                }).then(mapResponseBody);
            }
        },
        charge: {
            approveAll (relationshipId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/approve"),
                    urlForSentry: '/billing/:id/charge/approve',
                    data: {}
                }).then(mapResponseBody);
            },
            async approve (relationshipId, chargeId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/").concat(chargeId, "/approve"),
                    urlForSentry: '/billing/:id/charge/:id/approve'
                });
            },
            async delete (relationshipId, chargeId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/billing/".concat(relationshipId, "/charge/").concat(chargeId, "/cancel"),
                    urlForSentry: '/billing/:id/charge/:id/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.AdHocChargeNotFound)
                });
            },
            async deleteCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/cancel"),
                    urlForSentry: '/billing/:id/credit/:id/cancel'
                });
            },
            async pauseCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/pause"),
                    urlForSentry: '/billing/:id/credit/:id/pause'
                });
            },
            async resumeCredit (relationshipId, chargeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/".concat(relationshipId, "/credit/").concat(chargeId, "/resume"),
                    urlForSentry: '/billing/:id/credit/:id/resume'
                });
            }
        },
        service: {
            async togglePauseResumeBilling (relationshipId, serviceId, pause) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/pause"),
                    urlForSentry: '/relationship/:id/service/:id/pause',
                    data: {
                        pause
                    }
                });
            },
            nextBillingDate (relationshipId, serviceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/next-billing-date"),
                    urlForSentry: '/relationship/:id/service/:id/next-billing-date'
                }).then((res)=>timeModel.fromDateStampObject(res.data.date));
            },
            async getPromptSuggestions () {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: '/api/service-description/conversations/suggestions',
                    urlForSentry: '/api/service-description/conversations/suggestions'
                }).then(mapResponseBody);
            },
            async generateDescriptionFromTitle (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/service-description/conversations/title',
                    urlForSentry: '/api/service-description/conversations/title',
                    data
                }).then(mapResponseBody);
            },
            async generateDescriptionFromDescription (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/service-description/conversations/description',
                    urlForSentry: '/api/service-description/conversations/description',
                    data
                }).then(mapResponseBody);
            },
            async generateDescriptionFromMessage (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(data.conversationId, "/messages"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/messages",
                    data: {
                        type: data.type,
                        message: data.message
                    }
                }).then(mapResponseBody);
            },
            async generateDescriptionFromPreset (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(data.conversationId, "/messages"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/messages",
                    data: {
                        type: data.type,
                        preset: data.preset
                    }
                }).then(mapResponseBody);
            },
            async undoDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/undo"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/undo"
                }).then(mapResponseBody);
            },
            async acceptServiceDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/accept"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/accept",
                    data: {}
                }).then(mapResponseBody);
            },
            async rejectServiceDescription (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/reject"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/reject",
                    data: {}
                }).then(mapResponseBody);
            },
            async submitServiceDescriptionFeedback (conversationId, data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/service-description/conversations/".concat(conversationId, "/feedback"),
                    urlForSentry: "/api/service-description/conversations/:conversationId/feedback",
                    data
                }).then(mapResponseBody);
            }
        },
        notificationRewrite: {
            async startConversation (data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/notification-rewrite/conversations',
                    data
                }).then(mapResponseBody);
            },
            async continueConversation (conversationId, data) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/messages"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/messages',
                    data
                }).then(mapResponseBody);
            },
            async undoRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/undo"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/undo',
                    data: {}
                }).then(mapResponseBody);
            },
            async acceptRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/accept"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/accept',
                    data: {}
                }).then(mapResponseBody);
            },
            async rejectRewrite (conversationId) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/notification-rewrite/conversations/".concat(conversationId, "/reject"),
                    urlForSentry: '/api/notification-rewrite/conversations/:conversationId/reject',
                    data: {}
                }).then(mapResponseBody);
            }
        },
        billing: {
            submitCharges (relationshipId, data) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.External,
                    url: "/billing/".concat(relationshipId, "/submit"),
                    urlForSentry: '/billing/:id/submit',
                    data,
                    disableSentry: (err)=>new BillingErrorResponse(err).isStaleBillingData() || err.hasServerError(ServerError.InvalidServiceStatusForBilling) || new RelationshipErrorResponse(err).isSignatureRefusal()
                }).then(mapResponseBody);
            },
            previewTotals (relationshipId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'query',
                    url: "/billing/".concat(relationshipId, "/totals-preview"),
                    urlForSentry: '/billing/:id/totals-preview',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.NoInvoiceForBillingDate,
                            ServerError.InvoiceNegativeTotal,
                            ServerError.TotalsPreviewRequestInvalid,
                            ServerError.MaxChargesExceeded,
                            ServerError.TotalsPreviewProjectionFailed
                        ])
                }).then(mapResponseBody);
            },
            getAdHocScheduledData (relationshipId, date) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/adhoc-scheduled-data"),
                    urlForSentry: '/billing/:id/adhoc-scheduled-data',
                    params: {
                        date
                    }
                }).then((res)=>res.data);
            },
            addCredit (relationshipId, data) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.External,
                    url: "/billing/".concat(relationshipId, "/credit"),
                    urlForSentry: '/billing/:id/credit',
                    data
                }).then(mapResponseBody);
            },
            scheduledBillingAttachments: {
                add (relationshipId, issueDate, attachments) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/billing/".concat(relationshipId, "/attach"),
                        urlForSentry: '/billing/:id/attach',
                        data: {
                            issueDate: {
                                year: issueDate.getFullYear(),
                                month: issueDate.getMonth() + 1,
                                day: issueDate.getDate()
                            },
                            attachments
                        }
                    }).then(mapResponseBody);
                },
                remove (relationshipId, issueDate, attachmentId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'delete',
                        url: "/billing/".concat(relationshipId, "/attachments/").concat(attachmentId),
                        urlForSentry: '/billing/:id/attachments/:attachmentId',
                        params: {
                            issueDate: timeModel.toDatestampArray(issueDate).join(',')
                        }
                    }).then(mapResponseBody);
                },
                async getDownloadUrl (relationshipId, attachmentId) {
                    const res = await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/billing/".concat(relationshipId, "/attachments/").concat(attachmentId, "/download"),
                        urlForSentry: '/billing/:id/attachments/:attachmentId/download'
                    });
                    return res.data.url;
                }
            },
            bulk: {
                upload (fileContent) {
                    return sendRequest({
                        method: 'post',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload",
                        data: fileContent,
                        config: {
                            headers: {
                                'Content-Type': 'text/csv'
                            }
                        }
                    }).then(mapResponseBody);
                },
                uploadResult (bulkUploadId) {
                    return sendRequest({
                        method: 'get',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload/".concat(bulkUploadId, "/results"),
                        urlForSentry: 'bulk/upload/:bulkUploadId/results'
                    }).then(mapResponseBody);
                },
                billClients (bulkUploadId) {
                    return sendRequest({
                        method: 'post',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/upload/".concat(bulkUploadId, "/submit"),
                        urlForSentry: '/billing/bulk/upload/:bulkUploadId/submit',
                        data: bulkUploadId,
                        config: {
                            headers: {
                                'Content-Type': 'text/csv'
                            }
                        },
                        disableSentry: (err)=>err.hasServerError(ServerError.BulkBillAlreadyInProgress)
                    }).then(mapResponseBody);
                },
                getBillClientsResult (bulkUploadId) {
                    return sendRequest({
                        method: 'get',
                        apiHost: APIHost.Mgmt,
                        url: "/billing/bulk/billing/".concat(bulkUploadId, "/results"),
                        urlForSentry: '/billing/bulk/billing/:bulkUploadId/results'
                    }).then(mapResponseBody);
                }
            },
            getBillingSchedule (data, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/billing/billing-schedule",
                    urlForSentry: '/billing/billing-schedule',
                    data,
                    params
                }).then(mapResponseBody);
            },
            getBillingTimeline (data, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'query',
                    url: "/billing/billing-timeline",
                    data,
                    params
                }).then(mapResponseBody).then(toBillingTimeline);
            }
        },
        billCommand: {
            async remove (relationshipId, serviceId, billCommandId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/service/").concat(serviceId, "/bill-command/").concat(billCommandId, "/cancel"),
                    urlForSentry: '/relationship/:id/service/:id/bill-command/:id/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.BillCommandNotFoundInService)
                });
            }
        },
        texts: {
            getLegalTermsRichTextContent (sourceId, textId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/texts/".concat(sourceId, "/").concat(textId),
                    urlForSentry: '/texts/:sourceId/:id',
                    disableSentry: (err)=>err.hasServerError(ServerError.TextStorageNotFound)
                }).then(mapResponseBody);
            }
        },
        relationship: {
            serviceBillingDetails (service, effectiveDate) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/service-billing-details",
                    urlForSentry: '/relationship/service-billing-details',
                    data: {
                        service,
                        effectiveDate
                    }
                }).then((res)=>{
                    return {
                        periods: res.data.periods,
                        nextBillingDate: res.data.nextBillingDate ? fromDateStampObject(res.data.nextBillingDate) : null
                    };
                });
            },
            getBillingSchedule (relationshipId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/billing-schedule"),
                    urlForSentry: '/relationship/:id/billing-schedule',
                    params
                }).then(mapResponseBody);
            },
            getBillingTimeline (relationshipId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/billing-timeline"),
                    urlForSentry: '/relationship/:id/billing-timeline',
                    params
                }).then(mapResponseBody).then(toBillingTimeline);
            },
            previewAmendmentBillingTimeline (relationshipId, data, signal) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'query',
                    url: "/relationship/".concat(relationshipId, "/billing-timeline/preview"),
                    urlForSentry: '/relationship/:id/billing-timeline/preview',
                    data,
                    config: signal ? {
                        signal
                    } : undefined
                }).then(mapResponseBody).then((preview)=>({
                        current: toBillingTimeline(preview.current),
                        amended: toBillingTimeline(preview.amended)
                    }));
            },
            getServiceNames (businessId, isVendor) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/relationships-summary-mv/service-names"),
                    urlForSentry: '/business/:id/relationships-summary-mv/service-names',
                    params: {
                        party: isVendor ? 'vendor' : 'customer',
                        status: "".concat(ServiceStatusType.Approved, ",").concat(ServiceStatusType.Terminated)
                    }
                }).then((res)=>res.data.map((param)=>{
                        let { serviceName, isBundle } = param;
                        return {
                            name: serviceName,
                            isBundle
                        };
                    }));
            },
            getSummaries (config) {
                const { businessId, draftIds } = config;
                const askedBy = (draftIds === null || draftIds === void 0 ? void 0 : draftIds.length) ? {
                    method: 'query',
                    data: {
                        draftIds
                    }
                } : {
                    method: 'get'
                };
                return sendRequest(_object_spread({
                    apiHost: APIHost.Mgmt,
                    url: "/business/".concat(businessId, "/relationships-summary-mv"),
                    urlForSentry: '/business/:id/relationships-summary-mv',
                    params: buildRelationshipsParamsFilter(config)
                }, askedBy)).then((res)=>(res.data || []).map(relationshipSummaryModel.fromDTO));
            },
            searchSummaries (param) {
                let { businessId, filter, isVendor, page = 1, limit = 50, includeOrArmMatchCounts, signal } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/relationships-summary-mv/search"),
                    urlForSentry: '/business/:id/relationships-summary-mv/search',
                    params: _object_spread({
                        page,
                        limit,
                        party: isVendor ? 'vendor' : 'customer'
                    }, includeOrArmMatchCounts && {
                        includeOrArmMatchCounts
                    }),
                    data: {
                        filter
                    },
                    config: signal ? {
                        signal
                    } : undefined
                }).then((res)=>{
                    var _res_data;
                    const data = (_res_data = res.data) !== null && _res_data !== void 0 ? _res_data : {
                        entries: [],
                        page,
                        limit,
                        totalCount: 0
                    };
                    return {
                        entries: (data.entries || []).map(relationshipSummaryModel.fromDTO),
                        page: data.page,
                        limit: data.limit,
                        totalCount: data.totalCount,
                        orArmMatchCounts: data.orArmMatchCounts
                    };
                });
            },
            getProposalsReportSummaries (param) {
                let { isVendor, businessId, filters } = param;
                const filterParams = getProposalsReportFilterParams(filters, isVendor);
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/relationships-summary-mv"),
                    urlForSentry: '/business/:id/relationships-summary-mv',
                    params: _object_spread({}, filterParams)
                }).then((res)=>(res.data || []).map(relationshipSummaryModel.fromDTO));
            },
            /** Returns null if didn't connect any payment-method. */ getSelectedPaymentMethod (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/funding-source"),
                    urlForSentry: '/relationship/:id/funding-source'
                }).then((res)=>res.data.details);
            },
            async changePaymentMethod (relationshipId, fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/funding-source/").concat(fundingSourceId),
                    urlForSentry: '/relationship/:id/funding-source/:id',
                    replayRefusal: {
                        isReplayable: (err)=>new PaymentMethodErrorResponse(err).isNotVerifiedRejection(),
                        delaysMs: BANK_VERIFICATION_REPLAY_DELAYS_MS
                    }
                });
            },
            async disconnectPaymentMethod (relationshipId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/disconnect-payment-method"),
                    urlForSentry: '/relationship/:id/disconnect-payment-method'
                });
            },
            createAdHoc (clientContactId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/relationship/create-adhoc',
                    data: {
                        clientContactId
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.BuyerUserAssociatedWithVendor) || err.hasServerError(ServerError.DuplicateNativeClientContact)
                }).then(mapResponseBody);
            },
            createFromDraft (draftId) {
                let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: opts.externallySign ? '/relationship/approve/draft/vendor' : '/relationship/create-from-draft',
                    data: {
                        draftId,
                        notifyPolicy: opts.sendSilently ? 'silent' : 'notify'
                    },
                    disableSentry: (e)=>new RelationshipErrorResponse(e).isKnown()
                }).then(mapResponseBody);
            },
            get (relationshipId) {
                let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                const { resolvePaymentMethod, disable403Handling } = options;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId),
                    urlForSentry: '/relationship/:id',
                    params: {
                        resolvePaymentMethod
                    },
                    disable403Handling
                }).then((res)=>relationshipModel.fromDTO(res.data));
            },
            getProposalFromDraft (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(id, "/proposal-from-draft"),
                    urlForSentry: '/relationship/:id/proposal-from-draft'
                }).then(mapResponseBody).then(relationshipModel.fromDTO);
            },
            saveReferenceFields (agreementId, referenceFields) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v2/agreements/".concat(agreementId, "/reference-fields"),
                    urlForSentry: '/v2/agreements/:id/reference-fields',
                    data: {
                        referenceFields
                    },
                    disableSentry: (err)=>err.hasStatus([
                            409
                        ])
                }).then(mapResponseBody);
            },
            /** Returns the new relationship version. */ async update (relationshipId, body) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/update"),
                    urlForSentry: "/relationship/:id/update",
                    data: body,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.ServiceEffectiveDateMustBeInFuture,
                            ServerError.CannotUpdateWithUnsentChanges
                        ])
                });
                return res.data;
            },
            async updateTitle (relationshipId, title) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v2/agreements/".concat(relationshipId, "/title"),
                    urlForSentry: '/v2/agreements/:id/title',
                    data: {
                        title
                    }
                });
            },
            /** Returns the new amendment draft version. */ async saveAmendments (relationshipId, body) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v2/agreements/".concat(relationshipId, "/amendments/draft"),
                    urlForSentry: "/v2/agreements/:id/amendments/draft",
                    data: {
                        draftChanges: body.draftChanges.map(amendmentDraftChangeToDTO),
                        version: body.draftVersion,
                        serviceNotes: body.serviceNotes
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.MismatchingRelationshipVersion) || err.hasServerError(ServerError.SignatureDocumentFrozen)
                });
                return res.data.version;
            },
            async applyAmendments (relationshipId, body) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/agreements/".concat(relationshipId, "/amendments/apply-draft"),
                    urlForSentry: "/v2/agreements/:id/amendments/apply-draft",
                    data: body,
                    disableSentry: (err)=>err.hasServerError(ServerError.MismatchingRelationshipVersion) || err.hasServerError(ServerError.SignatureDocumentFrozen)
                });
            },
            async approve (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/buyer"),
                    urlForSentry: '/relationship/:id/approve/buyer',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned
                        ])
                });
            },
            async signatoryApprove (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                const { fundingSourceId } = additionalData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/signatory"),
                    urlForSentry: '/relationship/:id/approve/signatory',
                    data: {
                        version,
                        fundingSourceId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UserAlreadySigned
                        ])
                });
            },
            async signatoryApproveV2 (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/signatory/v2"),
                    urlForSentry: '/relationship/:id/approve/signatory/v2',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: isExpectedApproveRefusal
                });
            },
            async approveV2 (relationshipData, additionalData) {
                const { id, version } = relationshipData;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(id, "/approve/buyer/v2"),
                    urlForSentry: '/relationship/:id/approve/buyer/v2',
                    data: _object_spread({
                        version
                    }, additionalData),
                    disableSentry: isExpectedApproveRefusal
                });
            },
            getSignatureDocumentUrl (relationshipId, legalTermsId, variant) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/legal-terms/").concat(legalTermsId, "/document"),
                    urlForSentry: '/relationship/:id/legal-terms/:legalTermsId/document',
                    params: {
                        variant
                    },
                    disableSentry: (err)=>new SignatureErrorResponse(err).isCompletedDocumentNotAvailable()
                }).then((res)=>res.data.url);
            },
            async downloadSignedDocumentsZip (relationshipId) {
                const response = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/signed-documents.zip"),
                    urlForSentry: '/relationship/:id/signed-documents.zip',
                    config: {
                        responseType: 'blob'
                    },
                    disableSentry: (err)=>err.status === 404
                });
                return {
                    blob: response.data,
                    filename: "".concat(relationshipId, "-signed-documents.zip")
                };
            },
            getSignatureFields (relationshipId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/legal-terms/").concat(legalTermsId, "/signature/fields"),
                    urlForSentry: '/relationship/:id/legal-terms/:legalTermsId/signature/fields',
                    disableSentry: (err)=>new SignatureErrorResponse(err).isExpectedFieldsRefusal(),
                    disable403Handling: true
                }).then(mapResponseBody);
            },
            createSigningSession (relationshipId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/legal-terms/").concat(legalTermsId, "/signing-session"),
                    urlForSentry: '/relationship/:id/legal-terms/:legalTermsId/signing-session',
                    disable403Handling: isNotASignerRefusal,
                    disableSentry: isExpectedSigningSessionRefusal
                }).then(mapResponseBody);
            },
            async terminateAgreement (relationshipId, notification) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/terminate"),
                    urlForSentry: '/relationship/:id/terminate',
                    data: notification ? {
                        action: 'notify',
                        notification
                    } : {
                        action: 'silent'
                    },
                    disableSentry: (e)=>e.hasServerError(ServerError.RelationshipAlreadyTerminated)
                });
            },
            async requestPaymentMethod (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/request-payment-method"),
                    urlForSentry: '/relationship/:id/request-payment-method'
                });
            },
            getNextReminder (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/next-reminder"),
                    urlForSentry: '/relationship/:id/next-reminder'
                }).then(mapResponseBody);
            },
            async stopAutoApproveAmendments (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/stop-auto-approve-amendments"),
                    urlForSentry: '/relationship/:id/stop-auto-approve-amendments'
                });
            },
            getSingleUseApprovalLink (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    disableAuth: true,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/single-use-approval-link"),
                    urlForSentry: '/relationship/:id/single-use-approval-link',
                    disableSentry: (err)=>err.hasServerError(ServerError.RelationshipAlreadyApproved)
                }).then(mapResponseBody);
            },
            getProposalLinks (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/proposal-links"),
                    urlForSentry: '/relationship/:id/proposal-links',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.RelationshipAlreadyApproved,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then((res)=>{
                    return {
                        primary: RecipientModel.fromDTO(res.data.primary),
                        additionalRecipients: res.data.additionalRecipients.map(RecipientModel.fromDTO)
                    };
                });
            },
            mintProposalLinks (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/proposal-links"),
                    urlForSentry: '/relationship/:id/proposal-links'
                }).then((res)=>({
                        recipients: {
                            primary: RecipientModel.fromDTO(res.data.primary),
                            additionalRecipients: res.data.additionalRecipients.map(RecipientModel.fromDTO)
                        },
                        sharedLink: SharedLinkModel.fromDTO(res.data.sharedLink)
                    }));
            },
            getBillingControlData (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/control-data"),
                    urlForSentry: '/billing/:id/control-data'
                }).then((res)=>BillingControlDataModel.fromDTO(res.data));
            },
            async sendProposalApprovalReminder (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/reminder/proposal-approval"),
                    urlForSentry: '/relationship/:id/reminder/proposal-approval'
                });
            },
            async cancelProposal (relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/cancel"),
                    urlForSentry: '/relationship/:id/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.InvalidRelationshipStatus)
                });
            },
            withdrawForEditing (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/withdraw-for-editing"),
                    urlForSentry: '/relationship/:id/withdraw-for-editing',
                    disableSentry: (err)=>err.hasServerError(ServerError.InvalidRelationshipStatus)
                });
            },
            republish (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/republish"),
                    urlForSentry: '/relationship/:id/republish',
                    disableSentry: (err)=>new RelationshipErrorResponse(err).isSignatureRefusal() || err.hasServerError([
                            ServerError.ValidationError,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.UnresolvedTemplateVariables,
                            ServerError.ReviewerEmailIsSimilarToSellerEmail,
                            ServerError.ReviewerEmailIsSimilarToBuyerEmail,
                            ServerError.AdditionalRecipientUserAssociatedWithVendor,
                            ServerError.DuplicateReviewerEmail,
                            ServerError.ServiceEffectiveDateMustBeInFuture,
                            ServerError.ServiceEffectiveDateTooFarInPast,
                            ServerError.StageScheduleOnDateBeforeEffectiveDate,
                            ServerError.CounterSignatureMissing
                        ])
                });
            },
            cancelEdit (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/cancel-edit"),
                    urlForSentry: '/relationship/:id/cancel-edit'
                });
            },
            approveOnBehalfOfClient (proposalId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/proposals/".concat(proposalId, "/approve/vendor"),
                    urlForSentry: '/proposals/:id/approve/vendor',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidRelationshipStatus,
                            ServerError.CustomerAddressRequired
                        ]) || new RelationshipErrorResponse(err).isSignatureRefusal()
                }).then(mapResponseBody);
            },
            /** Will get a direct link to a relationship or an invoice, depending on the payload provided. */ getDirectLink (relationshipId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/direct-link"),
                    urlForSentry: '/relationship/:id/direct-link',
                    data: {
                        data: {
                            [data.type]: data.id
                        }
                    }
                }).then(mapResponseBody);
            },
            async approveAllInvoices (relationshipId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/approve-invoices"),
                    urlForSentry: '/relationship/:id/approve-invoices',
                    data: {}
                });
            },
            /** Updates the progressStatus of an agreement. */ async updateProgressStatus (agreementId, progressStatus) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(agreementId, "/approval-progress"),
                    urlForSentry: '/relationship/:id/approval-progress',
                    data: {
                        progressStatus
                    }
                });
            },
            approveAllAmendments (relationshipId, relationshipVersion, amendmentVersion) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/relationship/".concat(relationshipId, "/approve-amendments"),
                    urlForSentry: '/relationship/:id/approve-amendments',
                    data: {
                        version: relationshipVersion,
                        amendmentVersion
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.RelationshipAlreadyTerminated,
                            ServerError.NoAmendmentsToApprove
                        ])
                }).then(mapResponseBody);
            },
            approveAmendmentFromEmail (param) {
                let { approvalToken } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/approve-amendments-by-token",
                    data: {
                        approvalToken
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.MismatchingRelationshipVersion,
                            ServerError.RelationshipAlreadyTerminated,
                            ServerError.NoAmendmentsToApprove
                        ])
                }).then(mapResponseBody);
            },
            async exportAllProposalsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/proposals/export-csv'
                });
                return res.data;
            },
            async exportProposalsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposals/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            },
            async exportAllAgreementsCsv (args) {
                const params = buildAgreementsCsvExportParams({
                    search: args.search,
                    filters: args.filters
                });
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/agreements/export-csv',
                    params
                });
                return res.data;
            },
            async exportAgreementsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/agreements/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            }
        },
        draft: {
            createLegalTermsBuilderSession (draftId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-drafts/".concat(draftId, "/legal-terms/").concat(legalTermsId, "/builder-token"),
                    urlForSentry: '/proposal-drafts/:draftId/legal-terms/:legalTermsId/builder-token',
                    disableSentry: (err)=>err.hasServerError(EXPECTED_SIGNATURE_INSTANCE_OPEN_ERRORS)
                }).then(mapResponseBody);
            },
            syncLegalTermsSignature (draftId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-drafts/".concat(draftId, "/legal-terms/").concat(legalTermsId, "/signature/sync"),
                    urlForSentry: '/proposal-drafts/:draftId/legal-terms/:legalTermsId/signature/sync',
                    disableSentry: (err)=>err.hasServerError(EXPECTED_SIGNATURE_INSTANCE_SYNC_ERRORS)
                }).then(mapResponseBody);
            },
            getServiceNames () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/proposal-drafts/service-names'
                }).then((res)=>res.data.map((param)=>{
                        let { serviceName, isBundle } = param;
                        return {
                            name: serviceName,
                            isBundle
                        };
                    }));
            },
            getFullDrafts (params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts',
                    params
                }).then(mapResponseBody).then((drafts)=>drafts.map(DraftModel.fromDTO));
            },
            getSummaries () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts/summary'
                }).then(mapResponseBody);
            },
            search (param) {
                let { filter, page = 1, limit = 50, includeOrArmMatchCounts, signal } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposal-drafts/search',
                    params: _object_spread({
                        page,
                        limit
                    }, includeOrArmMatchCounts && {
                        includeOrArmMatchCounts
                    }),
                    data: filter,
                    config: signal ? {
                        signal
                    } : undefined
                }).then(mapResponseBody);
            },
            getArchivedSummaries () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/relationship-drafts/summary',
                    urlForSentry: '/relationship-drafts/summary?statuses=archived',
                    params: {
                        statuses: 'archived'
                    }
                }).then(mapResponseBody);
            },
            getById (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship-drafts/".concat(draftId),
                    urlForSentry: '/relationship-drafts/:id',
                    disableSentry: (e)=>e.status === 404
                }).then((res)=>DraftModel.fromDTO(res.data));
            },
            getBillingSchedule (draftId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship-drafts/".concat(draftId, "/billing-schedule"),
                    urlForSentry: '/relationship-drafts/:id/billing-schedule',
                    params,
                    disableSentry: (err)=>err.hasServerError(ServerError.BuyerUserAssociatedWithVendor)
                }).then(mapResponseBody);
            },
            getBillingTimeline (draftId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship-drafts/".concat(draftId, "/billing-timeline"),
                    urlForSentry: '/relationship-drafts/:id/billing-timeline',
                    params,
                    disableSentry: (err)=>err.hasServerError(ServerError.BuyerUserAssociatedWithVendor)
                }).then(mapResponseBody).then(toBillingTimeline);
            },
            save (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/relationship-drafts/',
                    data,
                    disableSentry: (err)=>new RelationshipErrorResponse(err).isSignatureRefusal() || err.hasServerError(ServerError.RelationshipDraftTitleTooLong)
                }).then(mapResponseBody);
            },
            duplicate (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/duplicate"),
                    urlForSentry: "/relationship-drafts/:id/duplicate",
                    data: {}
                }).then(mapResponseBody);
            },
            async deleteById (draftId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/relationship-drafts/".concat(draftId),
                    urlForSentry: '/relationship-drafts/:id',
                    disableSentry: (err)=>err.hasServerError(ServerError.DraftNotFound)
                });
            },
            createScheduledProposal (data) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/proposal-drafts/".concat(data.draftId, "/schedule"),
                    data: {
                        time: data.time
                    },
                    disableSentry: (err)=>new RelationshipErrorResponse(err).isSignatureRefusal() || err.hasServerError([
                            ServerError.DraftAlreadyScheduled,
                            ServerError.MissingDraftOwnerDetails,
                            ServerError.InvalidScheduleTime
                        ])
                }).then(mapResponseBody);
            },
            cancelScheduledProposal (data) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/proposal-drafts/".concat(data.draftId, "/cancel-schedule"),
                    data: {}
                }).then(mapResponseBody);
            },
            unarchive (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/unarchive"),
                    urlForSentry: '/relationship-drafts/:id/unarchive',
                    data: {}
                }).then(mapResponseBody);
            },
            archive (draftId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship-drafts/".concat(draftId, "/archive"),
                    urlForSentry: '/relationship-drafts/:id/archive',
                    data: {}
                }).then(mapResponseBody);
            },
            async exportAllDraftsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/proposal-drafts/export-csv'
                });
                return res.data;
            },
            bulkSendProposals (draftIds, jobName) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposal-drafts/bulk/send',
                    data: jobName == null ? {
                        draftIds
                    } : {
                        draftIds,
                        jobName
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.BulkSendProposalsAlreadyInProgress)
                }).then(mapResponseBody);
            },
            cancelBulkSendProposals (batchId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/proposal-drafts/bulk/send/".concat(encodeURIComponent(batchId), "/cancel"),
                    urlForSentry: '/v2/proposal-drafts/bulk/send/:batchId/cancel',
                    disableSentry: (err)=>err.hasServerError(ServerError.BatchNotInProgress)
                }).then(mapResponseBody);
            },
            getBulkSendProposalsStatus (batchId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v2/proposal-drafts/bulk/send/".concat(batchId),
                    urlForSentry: '/v2/proposal-drafts/bulk/send/:batchId'
                }).then(mapResponseBody);
            },
            async exportDraftsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v2/proposal-drafts/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            },
            bulkCreate: {
                getCurrentRun () {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: '/v2/proposal-drafts/bulk/current',
                        disableSentry: (err)=>err.status === 404
                    }).then(mapResponseBody);
                },
                createRun (data) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: '/v2/proposal-drafts/bulk',
                        data,
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.TemplateNotValid,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound,
                                ServerError.BulkRelationshipDraftsAlreadyOpen
                            ])
                    }).then(mapResponseBody);
                },
                changeTemplate (runId, data) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'put',
                        url: "/v2/proposal-drafts/bulk/".concat(runId, "/template"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/template',
                        data,
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.TemplateNotValid,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound,
                                ServerError.BulkRelationshipDraftsChangedConcurrently
                            ])
                    }).then(mapResponseBody);
                },
                downloadRecipientsCsv (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/recipients/download"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/recipients/download'
                    }).then((res)=>res.data);
                },
                recipientsUploadUrl (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/recipients/upload-url"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/recipients/upload-url',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsSubmitted,
                                ServerError.BulkRelationshipDraftsAbandoned
                            ])
                    }).then(mapResponseBody);
                },
                validateRecipients (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/recipients/validate"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/recipients/validate',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsFileUnreadable,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsFileTooLarge,
                                ServerError.BulkRelationshipDraftsSubmitted,
                                ServerError.BulkRelationshipDraftsAbandoned,
                                ServerError.BulkRelationshipDraftsChangedConcurrently
                            ])
                    }).then(mapResponseBody);
                },
                downloadPricingCsv (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/pricing/download"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/pricing/download',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsFileUnreadable,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsFileTooLarge,
                                ServerError.BulkRelationshipDraftsStepOrder,
                                ServerError.BulkRelationshipDraftsDuplicateServiceNames,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound
                            ])
                    }).then((res)=>res.data);
                },
                pricingUploadUrl (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/pricing/upload-url"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/pricing/upload-url',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsSubmitted,
                                ServerError.BulkRelationshipDraftsAbandoned,
                                ServerError.BulkRelationshipDraftsStepOrder
                            ])
                    }).then(mapResponseBody);
                },
                validatePricing (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/pricing/validate"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/pricing/validate',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsFileUnreadable,
                                ServerError.BulkRelationshipDraftsRepeatedClientIdColumn,
                                ServerError.BulkRelationshipDraftsRepeatedClientNameColumn,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsFileTooLarge,
                                ServerError.BulkRelationshipDraftsSubmitted,
                                ServerError.BulkRelationshipDraftsAbandoned,
                                ServerError.BulkRelationshipDraftsStepOrder,
                                ServerError.BulkRelationshipDraftsDuplicateServiceNames,
                                ServerError.BulkRelationshipDraftsPricingColumnsChanged,
                                ServerError.BulkRelationshipDraftsChangedConcurrently,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound
                            ])
                    }).then(mapResponseBody);
                },
                getPricing (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/pricing"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/pricing',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsFileUnreadable,
                                ServerError.BulkRelationshipDraftsRepeatedClientIdColumn,
                                ServerError.BulkRelationshipDraftsRepeatedClientNameColumn,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsFileTooLarge,
                                ServerError.BulkRelationshipDraftsStepOrder,
                                ServerError.BulkRelationshipDraftsDuplicateServiceNames,
                                ServerError.BulkRelationshipDraftsPricingColumnsChanged,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound
                            ])
                    }).then(mapResponseBody);
                },
                startRun (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/start"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/start',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsNotReady,
                                ServerError.BulkRelationshipDraftsStale,
                                ServerError.BulkRelationshipDraftsAlreadyInProgress,
                                ServerError.BulkRelationshipDraftsDuplicateServiceNames,
                                ServerError.BulkRelationshipDraftsSubmitted,
                                ServerError.BulkRelationshipDraftsAbandoned,
                                ServerError.BulkRelationshipDraftsChangedConcurrently,
                                ServerError.BulkRelationshipDraftsFileTooLarge,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsRepeatedClientIdColumn,
                                ServerError.BulkRelationshipDraftsRepeatedClientNameColumn,
                                ServerError.BulkRelationshipDraftsNotPermitted,
                                ServerError.ProposalTemplateDeleted,
                                ServerError.ProposalTemplateNotFound
                            ]),
                        disable403Handling: (err)=>err.hasServerError(ServerError.BulkRelationshipDraftsNotPermitted)
                    }).then(mapResponseBody);
                },
                cancelRun (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/cancel"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/cancel',
                        disableSentry: (err)=>err.hasServerError(ServerError.BatchNotInProgress) || err.hasServerError(ServerError.BulkRelationshipDraftsNotFound)
                    }).then(mapResponseBody);
                },
                getResults (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/results"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/results',
                        disableSentry: (err)=>err.status === 404 || err.status === 403
                    }).then(mapResponseBody);
                },
                getRecipients (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'get',
                        url: "/v2/proposal-drafts/bulk/".concat(encodeURIComponent(runId), "/recipients"),
                        urlForSentry: '/v2/proposal-drafts/bulk/:runId/recipients',
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.BulkRelationshipDraftsFileUnreadable,
                                ServerError.BulkRelationshipDraftsTooManyClients,
                                ServerError.BulkRelationshipDraftsFileTooLarge
                            ])
                    }).then(mapResponseBody);
                },
                closeRun (runId) {
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'delete',
                        url: "/v2/proposal-drafts/bulk/close/".concat(encodeURIComponent(runId)),
                        urlForSentry: '/v2/proposal-drafts/bulk/close/:runId',
                        disableSentry: (err)=>err.status === 409
                    }).then(mapResponseBody);
                }
            }
        },
        client: {
            getAll () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/customers'
                }).then(mapResponseBody).then((body)=>body.customers.map((param)=>{
                        let { id, name, clientType, isSample, status, owner, contactsCount, archiveEligibility, deleteEligibility, taxSettings, isMultiVendorCustomer } = param;
                        return new ClientModel({
                            clientId: id,
                            name,
                            clientType,
                            isSample,
                            status: status !== null && status !== void 0 ? status : 'active',
                            owner,
                            contactsCount,
                            archiveEligibility,
                            deleteEligibility,
                            taxSettings,
                            isMultiVendorCustomer
                        });
                    }));
            },
            getContacts (clientId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v3/contacts?customerId=".concat(encodeURIComponent(clientId)),
                    urlForSentry: '/v3/contacts'
                }).then(mapResponseBody).then((body)=>body.contacts.map((c)=>new ContactModel(c)));
            },
            getById (clientId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/customers/".concat(encodeURIComponent(clientId)),
                    urlForSentry: '/customers/:customerId',
                    disableSentry: isCustomerNotFound
                }).then(mapResponseBody);
            },
            getTaxExemptionCertificateUrl (clientId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/customers/".concat(encodeURIComponent(clientId), "/tax-exemption-certificate/download"),
                    urlForSentry: '/customers/:customerId/tax-exemption-certificate/download'
                }).then(mapResponseBody).then((param)=>{
                    let { url } = param;
                    return url;
                });
            },
            getTaxExemptionImpact (clientId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/customers/".concat(encodeURIComponent(clientId), "/tax-exemption-impact"),
                    urlForSentry: '/customers/:customerId/tax-exemption-impact'
                }).then(mapResponseBody).then((param)=>{
                    let { taxedAgreements } = param;
                    return taxedAgreements;
                });
            },
            getAgreementsOverview (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/customers/".concat(encodeURIComponent(customerId), "/agreements-overview"),
                    urlForSentry: '/customers/:customerId/agreements-overview'
                }).then(mapResponseBody);
            },
            create (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers',
                    data
                }).then(mapResponseBody).then((param)=>{
                    let { customerId } = param;
                    return {
                        clientId: customerId
                    };
                });
            },
            async update (clientId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/customers/".concat(clientId),
                    urlForSentry: '/customers/:clientId',
                    data,
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.MultiVendorCustomerNotEditable
                        ])
                });
            },
            bulkImport (csv) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers/bulk/create',
                    data: csv,
                    config: {
                        headers: {
                            'Content-Type': 'text/csv'
                        }
                    },
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.BulkImportUnreadableFile,
                            ServerError.BulkImportMissingColumns,
                            ServerError.BulkImportInvalidColumns,
                            ServerError.BulkImportTooLarge,
                            ServerError.BulkImportEmptyFile
                        ])
                }).then(mapResponseBody).then(bulkImportClientsResponseFromDTO);
            },
            async exportAllClientsCsv () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/customers/export-csv'
                });
                return res.data;
            },
            async exportClientsCsvByIds (ids) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers/export-csv',
                    data: {
                        ids
                    }
                });
                return res.data;
            },
            createContact (clientId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v3/contacts',
                    data: _object_spread_props(_object_spread({}, data), {
                        customerId: clientId
                    }),
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            },
            updateContact (clientId, contactId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v3/contacts/".concat(contactId),
                    urlForSentry: '/v3/contacts/:contactId',
                    data: _object_spread_props(_object_spread({}, data), {
                        customerId: clientId
                    }),
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.ContactVersionMismatch,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            },
            async archive (clientId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/customers/".concat(clientId, "/archive"),
                    urlForSentry: '/customers/:id/archive',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.FailedToArchiveCustomerDueToAgreement,
                            ServerError.CannotArchiveDeletedCustomer
                        ])
                });
            },
            async restore (clientId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/customers/".concat(clientId, "/unarchive"),
                    urlForSentry: '/customers/:id/unarchive',
                    disableSentry: (err)=>err.hasServerError(ServerError.CannotUnarchiveDeletedCustomer)
                });
            },
            async delete (clientId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/customers/".concat(clientId, "/delete"),
                    urlForSentry: '/customers/:id/delete',
                    disableSentry: (err)=>err.hasServerError(ServerError.FailedToDeleteCustomerDueToAgreement)
                });
            },
            async bulkArchive (request) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers/bulk-archive',
                    urlForSentry: '/customers/bulk-archive',
                    data: request
                });
                return res.data;
            },
            async bulkDelete (request) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/customers/bulk-delete',
                    urlForSentry: '/customers/bulk-delete',
                    data: request
                });
                return res.data;
            }
        },
        contactSummary: {
            list () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v4/contacts'
                }).then(mapResponseBody);
            },
            getById (contactId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v4/contacts',
                    params: {
                        contactId
                    }
                }).then(mapResponseBody).then(singleContactSummary);
            },
            async delete (contactId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/v4/contacts/".concat(contactId),
                    urlForSentry: '/v4/contacts/:contactId',
                    disableSentry: (e)=>e.hasServerError(ServerError.ContactHasAssociations)
                });
            },
            create (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v4/contacts',
                    data,
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError(ServerError.DuplicateContactIdentity)
                }).then(mapResponseBody);
            },
            async update (contactId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/v4/contacts/".concat(contactId),
                    urlForSentry: '/v4/contacts/:contactId',
                    data,
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContactIdentity,
                            ServerError.ContactIdentityNotEditable
                        ])
                });
            }
        },
        clientContact: {
            create (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/client-contacts',
                    data,
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.DuplicateNativeClientContact,
                            ServerError.ClientContactAlreadyExists,
                            ServerError.CustomerArchived,
                            ServerError.MultiVendorCustomerNotEditable
                        ])
                }).then(mapResponseBody);
            },
            async updateRole (clientContactId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/client-contacts/".concat(clientContactId),
                    urlForSentry: '/client-contacts/:clientContactId',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.ContactVersionMismatch)
                });
            }
        },
        contact: {
            getAll () {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/clients'
                }).then(mapResponseBody).then((contacts)=>contacts.map((c)=>new ContactModel(c)));
            },
            getById (contactId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/contacts/".concat(contactId),
                    urlForSentry: '/contacts/:id'
                }).then((res)=>new ContactModel(res.data));
            },
            update (contactId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/contacts/".concat(contactId),
                    urlForSentry: '/contacts/:id/update',
                    data,
                    disableSentry: (e)=>e.hasPhoneValidationError() || e.hasServerError([
                            ServerError.DuplicateContact,
                            ServerError.ContactVersionMismatch,
                            ServerError.ContactNotEditable,
                            ServerError.UserWithEmailAlreadyExists
                        ])
                }).then(mapResponseBody);
            },
            qboCreate (contactId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/qbo-create"),
                    urlForSentry: '/clients/:id/qbo-create',
                    disableSentry: (err)=>err.hasServerError(ServerError.DuplicateNameExists)
                }).then(mapResponseBody);
            },
            async delete (contactId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/clients/".concat(contactId, "/delete"),
                    urlForSentry: '/clients/:id/delete',
                    disableSentry: (err)=>err.hasServerError(ServerError.FailedToDeleteContactDueToExistingRelationships)
                });
            }
        },
        auth: {
            loginEmailStep (email, pageUrl) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/login/flow/".concat(email),
                    urlForSentry: '/login/flow/:email',
                    disableAuth: true,
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                }).then(mapResponseBody);
            },
            loginWithPassword (data) {
                const { email, password, utms, anchorReferrer } = data;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login',
                    data: {
                        email,
                        password,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': anchorReferrer
                        }
                    }
                }).then(mapResponseBody);
            },
            async xeroSignUp (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/xero/sign-up',
                    data
                });
                return res.data;
            },
            async xeroSignIn (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/xero/sign-in',
                    data
                });
                return res.data;
            },
            async qboSignUp (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/qbo/sign-up',
                    data
                });
                return res.data;
            },
            async qboSignIn (data) {
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/integrations/accounting/qbo/sign-in',
                    data
                });
                return res.data;
            },
            async signupOld (param) {
                let { fullName, email, password, utms, pageUrl } = param;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/signup-old',
                    data: {
                        fullName,
                        email,
                        password,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            409
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                });
                return res.data;
            },
            async signup (param) {
                let { fullName, email, password, utms, pageUrl } = param;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: '/signup',
                    data: {
                        fullName,
                        email,
                        password,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            409
                        ]),
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    }
                });
                return res.data;
            },
            async approveTos (userId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/user/".concat(userId, "/approve-tos"),
                    urlForSentry: '/user/:id/approve-tos'
                });
            },
            async setPassword (password) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/password/set',
                    data: {
                        password
                    },
                    forceAuthorizationHeader: true
                });
                return res.data;
            },
            getUserInfoV2 () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/user/v2/me?expand=worksAt",
                    disableSentry: (err)=>err.hasStatus([
                            401
                        ])
                }).then((res)=>res.data ? UserInfoModel.fromDTO(res.data) : null).catch((err)=>{
                    var _err_response;
                    if (((_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.status) === 401) {
                        // This will happen when the user is removed from his team by another team-member.
                        // Should trigger an automatic logout.
                        return null;
                    }
                    throw err;
                });
            },
            getUserVideos () {
                return sendRequest({
                    method: 'get',
                    url: "/users/videos",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            editUserDetails (userDetails) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me',
                    data: userDetails,
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ])
                }).then(mapResponseBody);
            },
            oauthSignIn (param) {
                let { provider, pageUrl, idToken, apple, utms, ensureEmail } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/oauth',
                    data: _object_spread_props(_object_spread({
                        provider,
                        id_token: idToken
                    }, apple ? {
                        apple
                    } : {}), {
                        utms,
                        ensureEmail
                    }),
                    config: {
                        headers: {
                            'Anchor-Referrer': pageUrl
                        }
                    },
                    disable403Handling: true,
                    disableSentry: isExpectedOauthSignInRejection
                }).then((res)=>res.data.authToken);
            },
            async resetPassword (email) {
                await sendRequest({
                    method: 'post',
                    url: '/user/forgot-password',
                    data: {
                        email
                    },
                    disableAuth: true,
                    apiHost: APIHost.Mgmt
                });
            },
            requestNewOTP (email) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login/otp/send',
                    data: {
                        email
                    },
                    //429 is ignored in Sentry
                    disableSentry: (err)=>err.hasStatus([
                            429
                        ]) || err.hasServerError(ServerError.OtpNotFound)
                }).then(mapResponseBody);
            },
            requestNewOTPForSignupFlow (email) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/signup/otp/resend-verification',
                    data: {
                        email
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.EmailAlreadyVerified)
                }).then(mapResponseBody);
            },
            validateOTP (email, otp, utms) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/login/otp',
                    data: {
                        email,
                        otp,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400,
                            429
                        ])
                }).then((res)=>res.data.authToken);
            },
            validateOTPForSignupFlow (email, otp, utms) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/signup/otp/verify',
                    data: {
                        email,
                        otp,
                        utms
                    },
                    disableSentry: (err)=>err.hasStatus([
                            400
                        ])
                }).then((res)=>res.data.authToken);
            }
        },
        video: {
            markVideoAsWatched (id) {
                return sendRequest({
                    method: 'post',
                    url: "/users/videos/".concat(id, "/watched"),
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            }
        },
        user: {
            switchBusiness (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/switch-workspace',
                    data: {
                        businessId
                    },
                    disable403Handling: true
                }).then((res)=>res.data.authToken);
            },
            async reorderWorkspaces (workspaceIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/reorder-workspaces',
                    data: {
                        workspaceIds
                    }
                });
            },
            async setWorkspaceVisibility (businessId, visibility) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/set-workspace-visibility',
                    data: {
                        businessId,
                        visibility
                    }
                });
            },
            startClientView (clientBusinessId, viewUserId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/me/view-client-portal/".concat(clientBusinessId),
                    urlForSentry: '/user/me/view-client-portal/:clientBusinessId',
                    data: viewUserId != null ? {
                        viewUserId
                    } : undefined,
                    disableSentry: (err)=>err.hasServerError(ServerError.ClientBusinessHasNoOwner)
                }).then((res)=>res.data.token);
            },
            exitClientView (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/user/me/exit-view-mode',
                    data: {
                        businessId
                    }
                }).then((res)=>res.data.token);
            },
            getUserClientData () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/client-data/user"
                }).then(mapResponseBody);
            },
            async setUserClientData (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/client-data/user",
                    data
                });
            },
            getBusinessClientData () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/client-data/business"
                }).then(mapResponseBody);
            },
            async setBusinessClientData (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/client-data/business",
                    data
                });
            },
            referral () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/user/referral',
                    urlForSentry: '/user/referral'
                }).then(mapResponseBody).then(ReferralModel.fromDTO);
            },
            updateBranding (businessId, branding) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/branding"),
                    urlForSentry: '/business/:id/branding',
                    data: branding
                }).then(mapResponseBody);
            },
            changeUserEmail (param) {
                let { email } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/change-email/otp",
                    data: {
                        email
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.EmailAlreadyRegistered
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            verifyEmailWithOTP (param) {
                let { otp, email } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/change-email/verify",
                    data: {
                        email,
                        otp
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.EmailAlreadyRegistered,
                            ServerError.InvalidOTP
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            getOTPCode () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/generic-otp/send",
                    disableSentry: (err)=>err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            verifyOTP (param) {
                let { otp } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/generic-otp/verify",
                    data: {
                        otp
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidOTP
                        ]) || err.isTooManyAttemptsError()
                }).then(mapResponseBody);
            },
            getTeamInvites () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/user/team-invites'
                }).then(mapResponseBody).then((res)=>res.invites.map(TeamInviteModel.fromDTO));
            },
            updateBuyer (userId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/".concat(userId, "/update-buyer"),
                    data
                }).then(mapResponseBody);
            },
            updateBuyerEmail (userId, email, customerBusinessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/user/".concat(userId, "/update-buyer-email"),
                    urlForSentry: '/user/:id/update-buyer-email',
                    data: {
                        email,
                        customerBusinessId
                    },
                    disableSentry: isHandledBuyerEmailRejection
                }).then(mapResponseBody);
            },
            async updateBuyerRole (userId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/user/".concat(encodeURIComponent(userId), "/update-buyer-role"),
                    urlForSentry: '/user/:id/update-buyer-role',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.TargetUserNotBuyer,
                            ServerError.DeactivatedBuyerRoleNotEditable
                        ])
                });
            },
            isLastOwner (userId, customerBusinessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/user/".concat(encodeURIComponent(userId), "/is-last-owner"),
                    urlForSentry: '/user/:id/is-last-owner',
                    params: {
                        customerBusinessId
                    }
                }).then((response)=>mapResponseBody(response).isLastOwner);
            }
        },
        vendor: {
            async createV4 (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/v4/business/vendor',
                    data: _object_spread_props(_object_spread({}, data), {
                        clientTimestamp: getNow().getTime()
                    })
                });
                return res.data;
            },
            async update (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/business/vendor/update',
                    data,
                    disableSentry: (err)=>new PaymentMethodErrorResponse(err).isStripeError()
                });
                return res.data;
            },
            async authorizeVendorBankAccount (param) {
                let { routingNumber, accountNumber } = param;
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/direct/authorize-account',
                    urlForSentry: '/direct/authorize-account',
                    data: {
                        routingNumber,
                        accountNumber
                    },
                    disable403Handling: (err)=>new PayoutErrorResponse(err).authorizationError() != null,
                    disableSentry: (err)=>new PayoutErrorResponse(err).authorizationError() != null
                });
            },
            async updateBusinessDetails (businessId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/details"),
                    urlForSentry: '/business/:id/details',
                    data
                });
            },
            async updateBusinessSettings (businessId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/settings"),
                    urlForSentry: '/business/:id/settings',
                    data
                });
            },
            async getBusinessSettings (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/settings"),
                    urlForSentry: '/business/:id/settings'
                });
                return BusinessSettingsModel.fromDTO(res.data);
            },
            async getSalesTax (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/sales-tax?includeMappingStatus=true"),
                    urlForSentry: '/business/:id/sales-tax'
                });
                return salesTaxConfigFromDTO(res.data);
            },
            async updateSalesTaxSettings (businessId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/business/".concat(businessId, "/sales-tax"),
                    urlForSentry: '/business/:id/sales-tax',
                    data
                });
            },
            async getSalesTaxComponents (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/sales-tax/components"),
                    urlForSentry: '/business/:id/sales-tax/components'
                });
                return res.data.components;
            },
            async createSalesTaxRate (businessId, data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/sales-tax/rates-with-components"),
                    urlForSentry: '/business/:id/sales-tax/rates-with-components',
                    data
                });
                return res.data;
            },
            async updateSalesTaxRate (businessId, rateId, data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: "/business/".concat(businessId, "/sales-tax/rates-with-components/").concat(rateId),
                    urlForSentry: '/business/:id/sales-tax/rates-with-components/:rateId',
                    data
                });
                return res.data;
            },
            getStripeHostedKYBUrl (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/stripe-hosted-kyb"),
                    urlForSentry: '/business/:id/stripe-hosted-kyb'
                }).then(mapResponseBody);
            },
            getKYBUpdatedFlowInquiry (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/inquiry-updated-flow"),
                    urlForSentry: '/business/:id/inquiry-updated-flow'
                }).then(mapResponseBody);
            },
            getTaxUpdateInquiry (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/inquiry-tax-update"),
                    urlForSentry: '/business/:id/inquiry-tax-update'
                }).then(mapResponseBody);
            },
            async getKYBStatus (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/kyb-status"),
                    urlForSentry: '/business/:id/kyb-status'
                });
                return res.data;
            },
            getTaxInfo (businessId) {
                return sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/tax-info"),
                    urlForSentry: '/business/:id/tax-info'
                }).then(mapResponseBody);
            }
        },
        ofacScreening: {
            async createInquiry (data) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'post',
                    url: '/ofac-screening-inquiry',
                    data
                });
                return res.data;
            },
            async getUserStatus (userId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/user/".concat(userId, "/ofac-screening-status"),
                    urlForSentry: '/user/:id/ofac-screening-status'
                });
                return new LatestOFACSanctionStatusModel(res.data);
            },
            async getBusinessStatus (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.KYB,
                    method: 'get',
                    url: "/business/".concat(businessId, "/ofac-screening-status"),
                    urlForSentry: '/business/:id/ofac-screening-status'
                });
                return new LatestOFACSanctionStatusModel(res.data);
            }
        },
        zapier: {
            getApiKey () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/zapier/api-key'
                }).then((res)=>res.data.apiKey);
            }
        },
        member: {
            async getAll (businessId) {
                const bid = businessId || selectors.user.businessId(getState());
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(bid, "/member"),
                    urlForSentry: '/business/:id/member'
                });
                return {
                    members: res.data.members.map((m)=>new MemberModel(m))
                };
            },
            async invite (data, businessIdOverride) {
                const businessId = businessIdOverride || selectors.user.businessId(getState());
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/invite"),
                    urlForSentry: '/business/:id/member/invite',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.UserAlreadyInBusiness,
                            ServerError.UserAlreadyAffiliatedWithBusiness,
                            ServerError.PendingInvitationAlreadyExists
                        ])
                });
                return res.data;
            },
            async resendInvitation (businessId, invitedUserId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/resend-invitation"),
                    urlForSentry: '/business/:id/member/resend-invitation',
                    data: {
                        invitedUserId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidTeamInvitation
                        ])
                });
            },
            async changeRole (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/member/change-role",
                    data
                });
            },
            async remove (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/business/member/deactivate',
                    data
                });
            },
            getAssignedRelationships (memberId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/count/user/".concat(memberId)
                }).then((res)=>res.data.count);
            },
            async reassignAllRelationships (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/relationship/reassign/all',
                    data,
                    disableSentry: (err)=>new TeamMemberErrorResponse(err).isBuyerReassignRefusal()
                });
            }
        },
        activityLog: {
            async get (relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/relationship/".concat(relationshipId, "/activity-log/v2"),
                    urlForSentry: '/relationship/:id/activity-log/v2'
                });
                return res.data.logs.map(ActivityLogEntryModel.make);
            },
            async getPreferences () {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: '/activity-log-preferences'
                });
                return res.data;
            },
            async savePreferences (input) {
                var _input_updatedAt;
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'post',
                    url: '/activity-log-preferences',
                    data: {
                        showSentNotifications: input.showSentNotifications,
                        updatedAt: (_input_updatedAt = input.updatedAt) !== null && _input_updatedAt !== void 0 ? _input_updatedAt : undefined
                    }
                });
                return res.data;
            }
        },
        invoice: {
            async void (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/void"),
                    urlForSentry: '/invoice/:id/void',
                    disableSentry: (err)=>new InvoiceErrorResponse(err).isStaleInvoiceStatus()
                });
            },
            async unvoid (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/unvoid"),
                    urlForSentry: '/invoice/:id/unvoid',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceNotVoided
                        ])
                });
            },
            async unmarkAsPaidExternally (invoiceId, options) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/unmark-paid-externally"),
                    urlForSentry: '/invoice/:id/unmark-paid-externally',
                    data: options ? {
                        notificationBehavior: options.notificationBehavior
                    } : undefined,
                    disableSentry: (err)=>err.hasServerError(ServerError.InvoiceNotPaidExternally)
                });
            },
            getByID (invoiceId, isCustomer) {
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId),
                    urlForSentry: '/invoice/:id',
                    params: {
                        view: isCustomer ? 'customer' : 'vendor'
                    }
                }).then((res)=>invoiceModel.fromLegacyDTO(!isCustomer, res.data));
            },
            getInvoice (invoiceId, isCustomer) {
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/".concat(invoiceId),
                    urlForSentry: '/invoices/:id',
                    params: {
                        view: isCustomer ? 'customer' : 'vendor'
                    },
                    disableSentry: (err)=>err.hasStatus([
                            404
                        ])
                }).then((res)=>invoiceModel.fromDTO(!isCustomer, res.data));
            },
            downloadPDF (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId, "/pdf"),
                    urlForSentry: '/invoice/:id/pdf'
                }).then((res)=>res.data.url);
            },
            async resend (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/resend"),
                    urlForSentry: '/invoice/:id/resend'
                });
            },
            async retryCharge (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/retry-payment"),
                    urlForSentry: '/invoice/:id/retry-payment',
                    disableSentry: (err)=>err.hasServerError(ServerError.InvoicePaymentNotRetryable)
                });
            },
            async markAsPaidExternally (invoiceId) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/paid-externally"),
                    urlForSentry: '/invoice/:id/paid-externally',
                    disableSentry: (err)=>err.hasServerError(ServerError.IncorrectStatusForPaidExternally)
                });
            },
            adhocApprovalLink (invoiceId) {
                return sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/adhoc-approval-link"),
                    urlForSentry: '/invoice/:id/adhoc-approval-link'
                }).then(mapResponseBody);
            },
            async approve (invoiceId) {
                const isCustomer = selectors.user.isClient(getState());
                if (!isCustomer) throw new Error('only clients can approve invoice');
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/approve"),
                    urlForSentry: '/invoice/:id/approve',
                    params: {
                        isCustomer
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                });
            },
            async getAll () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { relationshipId, sort, search, status, displayStatusV2, paymentStatus, paymentDetails, issuedOnSince, issuedOnUntil, dueDateSince, dueDateUntil, integration, xeroIntegration, pageSize, pageNumber } = filters;
                const isVendor = selectors.user.isVendor(getState());
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-summary-with-counters',
                    params: {
                        view: isVendor ? 'vendor' : 'customer',
                        relationship: relationshipId,
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        pageSize,
                        pageNumber,
                        search: search || undefined,
                        status: serializeArray(status),
                        displayStatusV2: serializeArray(displayStatusV2),
                        paymentStatus: serializeArray(paymentStatus),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        dueDateSince: serializeDate(dueDateSince),
                        dueDateUntil: serializeDate(dueDateUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        xeroIntegration: xeroIntegration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.xeroIntegration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined
                    }
                });
                return {
                    invoices: res.data.invoices.map((inv)=>invoiceModel.fromLegacyDTO(isVendor, inv)),
                    total: res.data.total,
                    qboStatusFilterCounters: res.data.qboStatusFilterCounters,
                    xeroStatusFilterCounters: res.data.xeroStatusFilterCounters,
                    displayStatusV2FilterCounters: res.data.displayStatusV2FilterCounters,
                    paymentDetailsFilterCounters: res.data.paymentDetailsFilterCounters
                };
            },
            async getInvoices () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { relationshipId, sort, search, displayStatusV2, paymentDetails, issuedOnSince, issuedOnUntil, dueDateSince, dueDateUntil, integration, xeroIntegration, pageSize, pageNumber } = filters;
                const isVendor = selectors.user.isVendor(getState());
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices',
                    params: {
                        view: isVendor ? 'vendor' : 'customer',
                        relationshipId,
                        sortField: externalSortField(sort === null || sort === void 0 ? void 0 : sort.field),
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        limit: pageSize,
                        page: pageNumber,
                        search: search || undefined,
                        status: serializeArray(displayStatusV2),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        dueDateSince: serializeDate(dueDateSince),
                        dueDateUntil: serializeDate(dueDateUntil),
                        qboIntegration: externalSyncFilter(integration),
                        xeroIntegration: externalSyncFilter(xeroIntegration),
                        includeCounters: 'true'
                    }
                });
                const counters = res.data.counters;
                const toLegacySyncCounters = (c)=>{
                    var _ref, _ref1;
                    return {
                        [AccountingSyncStatusFilter.Synced]: (_ref = c === null || c === void 0 ? void 0 : c.synced) !== null && _ref !== void 0 ? _ref : 0,
                        [AccountingSyncStatusFilter.Unsynced]: (_ref1 = c === null || c === void 0 ? void 0 : c.notSynced) !== null && _ref1 !== void 0 ? _ref1 : 0
                    };
                };
                return {
                    invoices: res.data.entries.map((inv)=>invoiceSummaryModel.fromDTO(isVendor, inv)),
                    total: res.data.totalCount,
                    qboStatusFilterCounters: toLegacySyncCounters(counters === null || counters === void 0 ? void 0 : counters.quickbooks),
                    xeroStatusFilterCounters: toLegacySyncCounters(counters === null || counters === void 0 ? void 0 : counters.xero),
                    displayStatusV2FilterCounters: counters === null || counters === void 0 ? void 0 : counters.displayStatus,
                    paymentDetailsFilterCounters: counters === null || counters === void 0 ? void 0 : counters.paymentDetails
                };
            },
            async getInvoicesByCustomer (customerId) {
                const isVendor = selectors.user.isVendor(getState());
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/invoices/customer/".concat(customerId),
                    urlForSentry: '/invoices/customer/:customerId'
                });
                return res.data.invoices.map((inv)=>invoiceModel.fromLegacyDTO(isVendor, inv));
            },
            async exportToCsv () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { sort, search, status, paymentStatus, issuedOnSince, issuedOnUntil, integration, view } = filters;
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-csv',
                    params: {
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        search: search || undefined,
                        status: serializeArray(status),
                        paymentStatus: serializeArray(paymentStatus),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        view
                    }
                });
                return res.data;
            },
            async exportToCsvV2 () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { sort, search, displayStatusV2, paymentDetails, issuedOnSince, issuedOnUntil, integration, view } = filters;
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoices-csv-v2',
                    params: {
                        sort: sort ? "".concat(sort.field, ",").concat(sort.type) : undefined,
                        search: search || undefined,
                        displayStatusV2: serializeArray(displayStatusV2),
                        paymentDetails: serializeArray(paymentDetails),
                        issuedOnSince: serializeDate(issuedOnSince),
                        issuedOnUntil: serializeDate(issuedOnUntil),
                        integration: integration === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : filters.integration === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined,
                        view
                    }
                });
                return res.data;
            },
            async stopPayment (invoiceId, reason) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/stop-payment"),
                    urlForSentry: '/invoice/:id/stop-payment',
                    data: {
                        reason
                    },
                    disableSentry: (err)=>new InvoiceErrorResponse(err).isStaleInvoiceStatus()
                });
            },
            async resumePayment (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/resume-payment"),
                    urlForSentry: '/invoice/:id/resume-payment',
                    disableSentry: (err)=>new InvoiceErrorResponse(err).isStaleInvoiceStatus()
                });
            },
            async refundPayment (invoiceId, reason) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/refund-payment"),
                    urlForSentry: '/invoice/:id/refund-payment',
                    data: {
                        reason
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceIsFullyRefunded,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                });
            },
            async refundPartialPayment (invoiceId, data) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Mgmt,
                    url: "/invoice/".concat(invoiceId, "/refund"),
                    urlForSentry: '/invoice/:id/refund',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceRefundAmountExceeds,
                            ServerError.InvoiceIsFullyRefunded,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                });
            },
            async calculateRefund (invoiceId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/calculate-refund"),
                    params,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceRefundAmountExceeds,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                }).then(mapResponseBody);
            },
            async getRefundState (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId, "/refund-state"),
                    urlForSentry: '/invoice/:id/refund-state'
                }).then(mapResponseBody);
            },
            async calculateRefundPerLine (invoiceId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/calculate-refund-lines"),
                    urlForSentry: '/invoice/:id/calculate-refund-lines',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceRefundAmountExceedsLineAmount,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                }).then(mapResponseBody);
            },
            async refundPerLine (invoiceId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/refund-lines"),
                    urlForSentry: '/invoice/:id/refund-lines',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceRefundAmountExceedsLineAmount,
                            ServerError.InvoiceIsFullyRefunded,
                            ServerError.InvoiceAlreadyHasOneRefundNotRefunded
                        ])
                });
            },
            async payNow (invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/pay-now"),
                    urlForSentry: '/invoice/:id/pay-now',
                    disableSentry: (err)=>new InvoiceErrorResponse(err).isStaleInvoiceStatus()
                });
            },
            async attachFiles (invoiceId, attachments) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/attach"),
                    urlForSentry: '/invoice/:id/attach',
                    data: {
                        attachments
                    }
                });
            },
            async getAttachmentDownloadUrl (invoiceId, attachmentId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/invoice/".concat(invoiceId, "/attachments/").concat(attachmentId, "/download"),
                    urlForSentry: '/invoice/:id/attachments/:attachmentId/download'
                });
                return res.data.url;
            },
            async removeAttachment (invoiceId, attachmentId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/invoice/".concat(invoiceId, "/attachments/").concat(attachmentId),
                    urlForSentry: '/invoice/:id/attachments/:attachmentId'
                });
            },
            disableAutoApprove (invoiceID) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    urlForSentry: '/invoice/:id/auto-approve/disable',
                    method: 'post',
                    url: "/invoice/".concat(invoiceID, "/auto-approve/disable")
                }).then(mapResponseBody);
            },
            getDigestFromQuery () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/vendor/".concat(businessId, "/payment-status-counts"),
                    urlForSentry: "/invoices/vendor/:id/payment-status-counts"
                }).then((response)=>InvoicesDigestModel.fromOptimizedQueryDTO(response.data));
            },
            approveInvoiceFromEmail (param) {
                let { approvalToken } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/approve-by-token",
                    data: {
                        approvalToken
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then(mapResponseBody);
            },
            approveAdHoc (param) {
                let { invoiceId, fundingSourceId, approvedLegalTermsId } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/approve-adhoc"),
                    urlForSentry: '/invoice/:invoiceId/approve-adhoc',
                    data: {
                        fundingSourceId,
                        approvedLegalTermsId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus
                        ])
                }).then(mapResponseBody);
            },
            retryAdHocPayment (param) {
                let { invoiceId, fundingSourceId } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/invoice/".concat(invoiceId, "/retry-adhoc-payment"),
                    urlForSentry: '/invoice/:invoiceId/retry-adhoc-payment',
                    data: {
                        fundingSourceId
                    },
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvoiceAlreadyApproved,
                            ServerError.InvoiceWasVoided,
                            ServerError.InvalidRelationshipStatus,
                            ServerError.InvoicePaymentNotRetryable
                        ])
                }).then(mapResponseBody);
            },
            resyncQbo (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/qbo/invoices/".concat(invoiceId, "/resync"),
                    urlForSentry: "/integrations/accounting/qbo/invoices/:id/resync"
                }).then(mapResponseBody);
            },
            resyncXero (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/xero/invoices/".concat(invoiceId, "/resync"),
                    urlForSentry: "/integrations/accounting/xero/invoices/:id/resync"
                }).then(mapResponseBody);
            },
            getResyncXeroStatus (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/xero/invoices/".concat(invoiceId, "/resync-status"),
                    urlForSentry: "/integrations/accounting/xero/invoices/:invoiceId/resync-status"
                }).then(mapResponseBody);
            },
            getResyncQboStatus (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/invoices/".concat(invoiceId, "/resync-status"),
                    urlForSentry: "/integrations/accounting/qbo/invoices/:invoiceId/resync-status"
                }).then(mapResponseBody);
            },
            async getMetrics (vendorId) {
                let filters = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                const serializeArray = (arr)=>arr && arr.length > 0 ? arr.join(',') : undefined;
                const serializeDate = serializeDateUtc;
                const mapAccountingFilter = (val)=>val === AccountingStatusFilter.Synced ? AccountingSyncStatusFilter.Synced : val === AccountingStatusFilter.NotSynced ? AccountingSyncStatusFilter.Unsynced : undefined;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/invoices/vendor/".concat(vendorId, "/metrics-v2"),
                    urlForSentry: '/invoices/vendor/:vendorId/metrics-v2',
                    params: {
                        issuedOnSince: serializeDate(filters.issuedOnSince),
                        issuedOnUntil: serializeDate(filters.issuedOnUntil),
                        displayStatusV2: serializeArray(filters.displayStatusV2),
                        paymentStatus: serializeArray(filters.paymentStatus),
                        paymentDetails: serializeArray(filters.paymentDetails),
                        search: filters.search || undefined,
                        relationship: filters.relationship || undefined,
                        integration: mapAccountingFilter(filters.integration),
                        xeroIntegration: mapAccountingFilter(filters.xeroIntegration),
                        dueDateSince: serializeDate(filters.dueDateSince),
                        dueDateUntil: serializeDate(filters.dueDateUntil)
                    }
                });
                return res.data;
            },
            async getDashboardPreferences () {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/invoice-dashboard-preferences'
                });
                return res.data;
            },
            async saveDashboardPreferences (input) {
                var _input_updatedAt;
                const res = await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/invoice-dashboard-preferences',
                    data: {
                        cards: input.cards,
                        updatedAt: (_input_updatedAt = input.updatedAt) !== null && _input_updatedAt !== void 0 ? _input_updatedAt : undefined
                    }
                });
                return res.data;
            }
        },
        serviceTemplate: {
            getAll (businessId, param) {
                let { includeDeleted, includeAdhocCharges, invoiceId, type } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template"),
                    urlForSentry: "/business/:id/service-template",
                    params: {
                        includeDeleted,
                        includeAdhocCharges,
                        invoiceId,
                        type: type.join(',')
                    }
                }).then((res)=>{
                    const { services } = res.data;
                    const raw = services.map(serviceTemplateModel.fromDTO);
                    const active = [];
                    const deleted = [];
                    raw.forEach((s)=>{
                        if (serviceTemplateModel.isDeleted(s)) {
                            deleted.push(s);
                        } else {
                            active.push(s);
                        }
                    });
                    return {
                        active,
                        deleted,
                        raw
                    };
                });
            },
            getById (businessId, serviceTemplateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template/").concat(serviceTemplateId),
                    urlForSentry: '/business/:id/service-template/:serviceId'
                }).then((res)=>serviceTemplateModel.fromDTO(res.data));
            },
            create (serviceTemplate, businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/service-template/create"),
                    urlForSentry: 'v2/business/:id/service-template/create',
                    data: serviceTemplateModel.toDTO(serviceTemplate),
                    disableSentry: (err)=>err.hasServerError(ServerError.ServiceTemplateAlreadyExists)
                }).then(mapResponseBody);
            },
            update (serviceTemplate, businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/service-template/update"),
                    urlForSentry: '/v2/business/:id/service-template/update',
                    data: serviceTemplateModel.toDTO(serviceTemplate),
                    disableSentry: (err)=>err.hasServerError(ServerError.ServiceTemplateAlreadyExists)
                }).then(()=>{});
            },
            async delete (id, businessId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/service-template/delete"),
                    urlForSentry: "/business/:id/service-template/delete",
                    data: {
                        id
                    }
                });
            },
            async exportServiceTemplatesToCsv (businessId, templateType) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/service-template/export-csv"),
                    urlForSentry: '/business/:id/service-template/export-csv',
                    params: {
                        type: templateType
                    }
                });
                return res.data;
            }
        },
        integration: {
            async getAll () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/list',
                    params: {
                        lightweight: false
                    }
                });
                return res.data.map(integrationModel.fromDTO);
            },
            async getSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/list',
                    params: {
                        lightweight: true
                    }
                });
                return res.data.map(integrationModel.fromDTO);
            }
        },
        accounting: {
            async connect (param) {
                let { redirectUrl, accounting } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/connect"),
                    data: {
                        redirectUrl
                    }
                });
                return res.data.linkUrl;
            },
            disconnect (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/disconnect")
                }).then(mapResponseBody);
            },
            getAccounts (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/".concat(accounting, "/accounts"),
                    disableSentry: (err)=>err.isBadGateway()
                }).then(mapResponseBody);
            },
            getCustomers (param) {
                let { accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/".concat(accounting, "/customers"),
                    disableSentry: (err)=>err.isBadGateway()
                }).then((res)=>res.data.customers.map(AccountingCustomerModel.fromDTO));
            },
            getItems (param) {
                let { accounting, type } = param;
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: "/integrations/accounting/".concat(accounting, "/items?type=").concat(type.join(',')),
                    urlForSentry: '/integrations/accounting/:accounting/items',
                    disableSentry: (err)=>err.hasErrorMessageIncluding('record not found') || err.isBadGateway()
                }).then((res)=>_object_spread_props(_object_spread({}, res.data), {
                        items: res.data.items.map((item)=>{
                            var _item_type;
                            return _object_spread_props(_object_spread({}, item), {
                                type: (_item_type = item.type) !== null && _item_type !== void 0 ? _item_type : AccountingItemType.Service
                            });
                        })
                    }));
            },
            async linkAccount (param) {
                let { data, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/link-account"),
                    data
                });
            },
            async linkServiceTemplate (param) {
                let { serviceTemplateId, integratedId, accounting, type } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/service-templates/").concat(serviceTemplateId, "/link"),
                    urlForSentry: "/integrations/accounting/:id/service-templates/:id/link",
                    data: {
                        integratedId,
                        type
                    }
                });
            },
            getTaxRates (param) {
                let { integration } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/".concat(integration, "/tax-rates"),
                    urlForSentry: '/integrations/accounting/:name/tax-rates',
                    disableSentry: (err)=>err.isBadGateway()
                }).then((res)=>res.data.taxRates.map((taxRate)=>_object_spread({
                            integratedId: taxRate.id,
                            name: taxRate.name,
                            rate: taxRate.rate
                        }, taxRate.description != null ? {
                            description: taxRate.description
                        } : {})));
            },
            async linkTaxMapping (param) {
                let { integration, taxUnitId, integratedId } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(integration, "/tax-mappings/").concat(taxUnitId, "/link"),
                    urlForSentry: '/integrations/accounting/:name/tax-mappings/:id/link',
                    data: {
                        integratedId
                    }
                });
            },
            async createContact (param) {
                let { contactId, accounting } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/clients/").concat(contactId, "/create"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/clients/:id/create"),
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.DuplicateNameExists,
                            ServerError.QboSubscriptionInactive
                        ])
                }).then(mapResponseBody);
            },
            async linkContact (param) {
                let { contactId, data, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/clients/").concat(contactId, "/link"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/clients/:id/link"),
                    data
                });
            },
            async syncInvoice (accounting, invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id")
                });
            },
            async retrySyncInvoice (accounting, invoiceId, includeTimeActivities) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/retry"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/retry"),
                    data: {
                        includeTimeActivities
                    }
                });
            },
            async syncRefund (accounting, invoiceId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/refund"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/refund")
                });
            },
            async syncPayment (param) {
                let { invoiceId, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/invoices/").concat(invoiceId, "/payment"),
                    urlForSentry: "/integrations/accounting/".concat(accounting, "/invoices/:id/payment")
                });
            },
            async syncPayout (param) {
                let { payoutId, accounting } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/payouts/").concat(payoutId)
                });
            },
            async updateSettings (param) {
                let { accounting, settings } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/accounting/".concat(accounting, "/update"),
                    data: settings
                });
            }
        },
        qbo: {
            async getClasses () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/classes",
                    disableSentry: (err)=>err.isBadGateway()
                });
                return res.data.classes;
            },
            async getTimeActivities (relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/accounting/qbo/relationships/".concat(relationshipId, "/time-activities"),
                    urlForSentry: '/integrations/accounting/qbo/relationships/:id/time-activities',
                    disableSentry: (err)=>err.isBadGateway()
                });
                return TimeActivitiesModel.fromDTO(res.data.services);
            }
        },
        practiceManagement: {
            async getMatchingCompanies (pmName, relationshipId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/matched-contacts-companies"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:relationshipId/matched-contacts-companies")
                });
                return res.data.companies || [];
            },
            async linkContact (pmName, relationshipId, companyId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/link-contact"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/link-contact"),
                    data: {
                        companyId
                    }
                }).then(mapResponseBody);
            },
            async createNewCompanyAndContact (pmName, relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/create-client"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/create-client"),
                    disableSentry: (err)=>err.hasErrorMessageIncluding(ServerError.KarbonPrimaryContactCardRequired)
                }).then(mapResponseBody);
            },
            async dismissSyncFailure (pmName, relationshipId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/".concat(pmName, "/relationships/").concat(relationshipId, "/dismiss-failure"),
                    urlForSentry: "/integrations/".concat(pmName, "/relationships/:id/dismiss-failure")
                }).then(mapResponseBody);
            }
        },
        financialCents: {
            async connect (redirectUrl) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents',
                    data: {
                        type: 'oauth',
                        oauth: {
                            redirectUrl
                        }
                    }
                });
                return res.data.oauth.linkUrl;
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/financial-cents/items',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (serviceTemplateId, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/financial-cents/items/link',
                    data: {
                        id: serviceTemplateId,
                        integratedIds
                    }
                });
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/financial-cents/users',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/financial-cents/".concat(serviceTemplateId, "/roles"),
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            }
        },
        serviceInvoices: {
            getByBusinessId (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/serviceBilling/".concat(businessId),
                    urlForSentry: '/serviceBilling/:businessId'
                }).then((res)=>(res.data.Entries || []).map(ServiceInvoiceModel.fromDTO));
            },
            getCredit () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/payments-credit'
                }).then((res)=>utils.centsToDollars(res.data.amount));
            },
            getDownloadURL (filename) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/file/").concat(filename),
                    urlForSentry: '/business/:id/file/:id'
                }).then(mapResponseBody);
            }
        },
        paymentMethod: {
            /**
             * Fetches the payment methods for a given agreement
             * When the vendor sends the request he will get an empty array.
             */ clientGet (vendorId) {
                return this.clientGetWithContext(vendorId).then((param)=>{
                    let { paymentMethods } = param;
                    return paymentMethods;
                });
            },
            /**
             * Same as clientGet but also surfaces `lastUsedFundingSourceId` so callers
             * that render a pre-selection UI (e.g. the ad-hoc invoice approval page) can
             * pre-select the most recently used payment method.
             */ clientGetWithContext (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/payment-method/vendor/".concat(vendorId),
                    urlForSentry: '/payment-method/vendor/:id'
                }).then((res)=>({
                        paymentMethods: res.data.paymentMethods,
                        lastUsedFundingSourceId: res.data.lastUsedFundingSourceId
                    }));
            },
            /**
             * Fetches the payment methods for a given agreement
             * When the client sends the request he will get an empty array.
             */ vendorGet (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/payment-method/customer/".concat(customerId),
                    urlForSentry: '/payment-method/customer/:id'
                }).then((res)=>res.data.paymentMethods);
            },
            clientAddToBusiness (paymentMethodId, vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(paymentMethodId, "/vendor/").concat(vendorId),
                    urlForSentry: '/payment-method/:id/vendor/:id'
                }).then((res)=>res.data.details);
            },
            vendorAddToBusiness (paymentMethodId, customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(paymentMethodId, "/customer/").concat(customerId),
                    urlForSentry: '/payment-method/:id/customer/:id'
                }).then((res)=>res.data.details);
            },
            async delete (fundingSourceId, vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/delete"),
                    urlForSentry: '/payment-method/funding-source/delete',
                    data: {
                        vendorId
                    }
                });
            },
            async clientDelete (fundingSourceId, vendorId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/by-customer/delete"),
                    urlForSentry: '/payment-method/funding-source/:id/by-customer/delete',
                    data: {
                        vendorId
                    },
                    disableSentry: isUndeletableFundingSource
                });
            },
            async vendorDelete (fundingSourceId, customerId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(fundingSourceId, "/by-vendor/delete"),
                    urlForSentry: '/payment-method/funding-source/:id/by-vendor/delete',
                    data: {
                        customerId
                    },
                    disableSentry: isUndeletableFundingSource
                });
            },
            createSetupIntent (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/".concat(vendorId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/:vendorId/create-payment-intent'
                }).then(mapResponseBody);
            },
            clientCreateSetupIntent (vendorId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/vendor/".concat(vendorId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/vendor/:vendorId/create-payment-intent'
                }).then(mapResponseBody);
            },
            vendorCreateSetupIntent (customerId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/customer/".concat(customerId, "/create-payment-intent"),
                    urlForSentry: '/payment-method/customer/:customerId/create-payment-intent'
                }).then(mapResponseBody);
            },
            async remindReauthorization (param) {
                let { fundingSourceId, customerId } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(encodeURIComponent(fundingSourceId), "/remind-reauthorization"),
                    urlForSentry: '/payment-method/funding-source/:id/remind-reauthorization',
                    data: {
                        customerId
                    },
                    disableSentry: isSettledDebitAuthorization
                });
            },
            async reauthorizeMandate (param) {
                let { fundingSourceId, vendorId } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/payment-method/funding-source/".concat(encodeURIComponent(fundingSourceId), "/reauthorize"),
                    urlForSentry: '/payment-method/funding-source/:id/reauthorize',
                    data: {
                        vendorId
                    },
                    disableSentry: isSettledDebitAuthorization
                });
            }
        },
        business: {
            async deletePostalAddress () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: '/business/postal-address'
                });
            },
            async updateOwnPostalAddress (data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'put',
                    url: '/business/postal-address',
                    data
                });
            },
            getLegalTerms () {
                const businessId = selectors.user.businessId(getState());
                if (!businessId) throw new Error('no business ID');
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/legal-terms"),
                    urlForSentry: '/business/:id/legal-terms'
                }).then(mapResponseBody);
            },
            async removeLegalTerms (legalTermsId) {
                const businessId = selectors.user.businessId(getState());
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/legal-terms/delete"),
                    urlForSentry: '/business/:id/legal-terms/delete',
                    data: {
                        id: legalTermsId
                    }
                });
            },
            async renameLegalTerms (legalTermsId, newName) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/rename"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/rename',
                    disableSentry: (err)=>err.hasServerError(ServerError.LegalTermsNameAlreadyExists),
                    data: {
                        name: newName
                    }
                }).then(mapResponseBody);
            },
            createLegalTermsOld (data) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/legal-terms/create"),
                    urlForSentry: '/business/:id/legal-terms/create',
                    data
                }).then(mapResponseBody);
            },
            createLegalTerms (data) {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/legal-terms/create"),
                    urlForSentry: '/v2/business/:id/legal-terms/create',
                    disableSentry: (err)=>err.hasServerError(ServerError.LegalTermsNameAlreadyExists),
                    data
                }).then(mapResponseBody);
            },
            createQuillLegalTerms (data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/quill",
                    disableSentry: (err)=>err.hasServerError(ServerError.LegalTermsNameAlreadyExists),
                    data
                }).then(mapResponseBody);
            },
            updateQuillLegalTerms (legalTermsId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/quill/".concat(legalTermsId),
                    urlForSentry: '/v2/business/legal-terms/quill/:legalTermsId',
                    disableSentry: (err)=>err.hasServerError(ServerError.LegalTermsNameAlreadyExists),
                    data
                }).then(mapResponseBody);
            },
            getLegalTermsUploadURL () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/legal-terms/upload-url"),
                    urlForSentry: '/business/:id/legal-terms/upload-url'
                }).then(mapResponseBody);
            },
            getLegalTermsDownloadURL (filename, businessId) {
                const actualBusinessId = businessId || selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(actualBusinessId, "/legal-terms/download-url"),
                    urlForSentry: '/business/:id/legal-terms/download-url',
                    params: {
                        filename
                    }
                }).then((res)=>res.data.url);
            },
            enableLegalTermsSignature (legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/signature"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/signature',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.LegalTermsPdfTooLargeForSignatures,
                            ServerError.LegalTermsPdfRejectedBySignatureProvider
                        ])
                }).then(mapResponseBody);
            },
            disableLegalTermsSignature (legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/signature/disable"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/signature/disable'
                }).then(mapResponseBody);
            },
            createLegalTermsBuilderSession (legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/builder-token"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/builder-token'
                }).then(mapResponseBody);
            },
            getLegalTermsSignatureFields (legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/signature/fields"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/signature/fields',
                    disableSentry: (err)=>new SignatureErrorResponse(err).isExpectedFieldsRefusal(),
                    disable403Handling: true
                }).then(mapResponseBody);
            },
            syncLegalTermsSignature (legalTermsId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/legal-terms/".concat(legalTermsId, "/signature/sync"),
                    urlForSentry: '/v2/business/legal-terms/:legalTermsId/signature/sync',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.LegalTermsSignatureRolesDrifted,
                            ServerError.LegalTermsSignatureNotEnabled
                        ])
                }).then(mapResponseBody);
            },
            getLogoUploadURL () {
                const businessId = selectors.user.businessId(getState());
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/logo/upload-url"),
                    urlForSentry: '/business/:id/logo/upload-url'
                }).then((res)=>res.data.url);
            },
            getUploadUrls (filesData) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/upload-urls",
                    urlForSentry: '/business/upload-urls',
                    data: {
                        filesData
                    }
                }).then(mapResponseBody);
            },
            getInfo (businessId) {
                return sendRequest({
                    method: 'get',
                    url: "/businesses/".concat(businessId),
                    urlForSentry: '/businesses/:id',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            updateBusinessOnboardingWidgetState (businessId, onboardingWidgetState) {
                return sendRequest({
                    method: 'post',
                    url: "/businesses/".concat(businessId, "/onboarding-state"),
                    urlForSentry: '/businesses/:id/onboarding-state',
                    apiHost: APIHost.Scout,
                    data: {
                        onboardingWidgetState
                    }
                }).then(mapResponseBody);
            },
            updateUiPreference (businessId, key, value) {
                return sendRequest({
                    method: 'post',
                    url: "/businesses/".concat(businessId, "/ui-preferences"),
                    urlForSentry: '/businesses/:id/ui-preferences',
                    apiHost: APIHost.Scout,
                    data: {
                        key,
                        value
                    }
                }).then(mapResponseBody);
            },
            async acceptTeamInvite (param) {
                let { invitationId, businessId } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/member/accept-invite"),
                    data: {
                        invitationId
                    },
                    urlForSentry: "/business/:businessId/member/accept-invite",
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.InvalidTeamInvitation
                        ])
                });
                return res.data;
            },
            async getAPIKeys (params) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: '/business/api-keys',
                    urlForSentry: '/business/api-keys',
                    params: {
                        sortField: params === null || params === void 0 ? void 0 : params.sortBy,
                        sortDirection: params === null || params === void 0 ? void 0 : params.sortDirection
                    }
                }).then(mapResponseBody);
            },
            async createAPIKey (name) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: '/business/api-key',
                    data: {
                        name
                    },
                    urlForSentry: '/business/api-key'
                }).then(mapResponseBody);
            },
            async regenerateAPIKey (id) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/regenerate",
                    urlForSentry: '/business/api-key/regenerate',
                    data: {
                        id
                    }
                }).then(mapResponseBody);
            },
            async renameAPIKey (param) {
                let { id, newName } = param;
                await sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/rename",
                    urlForSentry: '/business/api-key/rename',
                    data: {
                        id,
                        newName
                    },
                    disableSentry: (e)=>e.hasServerError([
                            ServerError.APIKeyNotFound,
                            ServerError.APIKeyRenameFailed
                        ])
                });
            },
            async deactivateAPIKey (id) {
                await sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/business/api-key/deactivate",
                    urlForSentry: '/business/api-keys/deactivate',
                    data: {
                        id
                    }
                });
            },
            updateBuyerCompany (customerBusinessId, companyName) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(customerBusinessId, "/update-client"),
                    data: {
                        companyName
                    }
                }).then(mapResponseBody);
            },
            async getActivityLogs () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/activity-log",
                    urlForSentry: '/business/activity-log',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.CustomerNotOwnedByVendor,
                            ServerError.MultiVendorCustomerNotEditable
                        ])
                });
                return res.data.logs.map(ActivityLogEntryModel.make);
            }
        },
        invoiceDraft: {
            getDates (relationshipId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/dates"),
                    urlForSentry: '/billing/:id/dates'
                }).then((res)=>InvoiceDraftDatesModel.fromDTO(res.data));
            },
            getByDate (relationshipId, date) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/billing/".concat(relationshipId, "/invoice-draft"),
                    urlForSentry: '/billing/:id/invoice-draft',
                    params: {
                        issueDate: timeModel.toDatestampArray(date).join(',')
                    }
                }).then((res)=>res.data && invoiceDraftModel.fromDTO(res.data));
            },
            getForAmendedService (relationshipId, service) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/relationship/".concat(relationshipId, "/amended-item-draft"),
                    urlForSentry: '/relationship/:id/amended-item-draft',
                    data: serviceModel.toDTO(service)
                }).then((res)=>({
                        issueDate: fromDateStampObject(res.data.issueDate),
                        lineItem: invoiceDraftModel.lineItemFromDTO(res.data.lineItem)
                    }));
            }
        },
        notification: {
            emailOnBehalf: {
                async connectEmailOnBehalf (param) {
                    let { businessId, provider, pageUrl } = param;
                    return sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/authorize-").concat(provider),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/authorize-".concat(provider),
                        config: {
                            headers: {
                                'anchor-return-to-path': pageUrl
                            }
                        }
                    }).then((response)=>response.data.url);
                },
                async completeGmailAuthorization (param) {
                    let { businessId, code, scope, csrfToken, redirectUri } = param;
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/complete-gmail-authorization"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/complete-gmail-authorization",
                        data: {
                            authorizationCode: code,
                            scope,
                            csrfToken,
                            redirectUri
                        },
                        disableSentry: (err)=>err.hasServerError([
                                ServerError.InvalidScopeMissingGmailSend
                            ]) || isExpectedEmailMailboxUnavailable(err)
                    });
                },
                async completeOutlookAuthorization (param) {
                    let { businessId, code, csrfToken, redirectUri } = param;
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'post',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of/complete-outlook-authorization"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of/complete-outlook-authorization",
                        data: {
                            authorizationCode: code,
                            csrfToken,
                            redirectUri
                        },
                        disableSentry: (err)=>err.hasServerError(ServerError.PersonalAccountNotSupported) || isExpectedEmailMailboxUnavailable(err)
                    });
                },
                async disconnectEmailOnBehalf (businessId) {
                    await sendRequest({
                        apiHost: APIHost.Mgmt,
                        method: 'delete',
                        url: "/business/".concat(businessId, "/settings/notifications/send-on-behalf-of"),
                        urlForSentry: "/business/:businessId/settings/notifications/send-on-behalf-of"
                    });
                }
            },
            async getWhileLoggedIn (notificationId, intent) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    params: {
                        intent
                    },
                    url: "/notification/auth/".concat(notificationId),
                    urlForSentry: '/notification/auth/:id',
                    disable403Handling: true,
                    disableSentry: (err)=>err.hasStatus([
                            401,
                            403
                        ]) || err.hasErrorMessageIncluding('record not found') || err.hasServerError([
                            ServerError.RecipientNotFound,
                            ServerError.InvalidTeamInvitation
                        ])
                });
                return res.data;
            },
            async getWhileLoggedOut (notificationId, intent) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    params: {
                        intent
                    },
                    url: "/notification/no-auth/".concat(notificationId),
                    urlForSentry: '/notification/no-auth/:id',
                    disableSentry: (err)=>err.hasStatus([
                            401,
                            403
                        ]) || err.hasErrorMessageIncluding('record not found'),
                    disable403Handling: true
                });
                return res.data;
            },
            async getNotificationSettings (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/settings/notifications"),
                    urlForSentry: "/business/:businessId/settings/notifications"
                }).then((res)=>_object_spread_props(_object_spread({}, res.data), {
                        sendOnBehalfOf: EmailOnBehalfSettingsModel.fromDTO(res.data.sendOnBehalfOf)
                    }));
            },
            async updateNotificationSettings (param) {
                let { data, businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/settings/notifications/").concat(notificationName),
                    urlForSentry: "/business/:businessId/settings/notifications/:notificationType",
                    data
                });
            },
            async updateNotificationSettingsV2 (param) {
                let { data, businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType",
                    data
                });
            },
            async setNotificationStatusV2 (param) {
                let { businessId, notificationName, status } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName, "/status"),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType/status",
                    data: {
                        status
                    }
                });
            },
            async revertNotificationSetting (param) {
                let { businessId, notificationName } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/v2/business/".concat(businessId, "/settings/notifications/").concat(notificationName, "/revert"),
                    urlForSentry: "/v2/business/:businessId/settings/notifications/:notificationType/revert"
                });
            }
        },
        inAppNotifications: {
            getAll () {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Mgmt,
                    url: '/business/notifications-mailbox'
                }).then(mapResponseBody);
            },
            async markAsSeen (notificationItemIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/notifications-mailbox/mark-as-seen",
                    data: {
                        ids: notificationItemIds
                    }
                });
            },
            async markAsRead (notificationItemId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/notifications-mailbox/mark-as-read",
                    data: {
                        id: notificationItemId
                    }
                });
            }
        },
        payout: {
            getById (payoutId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/v2/business/payouts/".concat(payoutId),
                    urlForSentry: '/v2/business/payouts/:id'
                }).then((res)=>PayoutModel.fromDTO(res.data));
            },
            async getAllFromScout () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { qboIntegration, xeroIntegration, pageSize, pageNumber, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts-summary-with-counters',
                    params: {
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        pageSize,
                        pageNumber,
                        search,
                        qboSyncStatus: qboIntegration,
                        xeroSyncStatus: xeroIntegration
                    }
                });
                return {
                    payouts: res.data.payouts.map((payout)=>PayoutModel.fromScoutDTO(payout)),
                    invoiceHeaders: res.data.invoiceHeaders,
                    total: res.data.total,
                    qboSyncStatusFilterCounters: res.data.qboSyncStatusFilterCounters,
                    xeroSyncStatusFilterCounters: res.data.xeroSyncStatusFilterCounters
                };
            },
            async getAllFromExternalApi () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                var _res_data_counters, _res_data_counters1;
                const { qboIntegration, xeroIntegration, pageSize, pageNumber, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts',
                    params: {
                        page: pageNumber,
                        limit: pageSize,
                        search,
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        qboIntegration,
                        xeroIntegration,
                        includeCounters: 'true'
                    }
                });
                return {
                    payouts: res.data.entries.map((payout)=>PayoutModel.fromExternalDTO(payout)),
                    invoiceHeaders: EXTERNAL_PAYOUT_INVOICE_HEADERS,
                    total: res.data.totalCount,
                    qboSyncStatusFilterCounters: (_res_data_counters = res.data.counters) === null || _res_data_counters === void 0 ? void 0 : _res_data_counters.quickbooks,
                    xeroSyncStatusFilterCounters: (_res_data_counters1 = res.data.counters) === null || _res_data_counters1 === void 0 ? void 0 : _res_data_counters1.xero
                };
            },
            async getPayoutInvoices (payoutId) {
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/payouts/".concat(payoutId, "/invoices"),
                    urlForSentry: '/payouts/:id/invoices'
                });
                return res.data.entries.map(PayoutModel.fromExternalInvoiceDTO);
            },
            async getSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/payouts-summary',
                    params: {
                        limit: 5,
                        withFuturePayouts: 'true'
                    }
                });
                return PayoutsSummaryModel.fromDTO(res.data);
            },
            async exportPayoutsToCsv () {
                let filters = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                const { qboIntegration, xeroIntegration, sort, search } = filters;
                const res = await sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: '/payouts-csv',
                    params: {
                        sortField: sort === null || sort === void 0 ? void 0 : sort.field,
                        sortDirection: sort === null || sort === void 0 ? void 0 : sort.type,
                        search,
                        qboSyncStatus: qboIntegration,
                        xeroSyncStatus: xeroIntegration
                    }
                });
                return res.data;
            }
        },
        report: {
            getMonthlyEarnings () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/report/monthly-earnings'
                }).then(mapResponseBody);
            },
            async getHistoricAgreementStats (businessId) {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/businesses/".concat(businessId, "/historic-agreements-stats"),
                    urlForSentry: '/businesses/:id/historic-agreements-stats'
                });
                return res.data.map((stats)=>new HistoricAgreementStatsModel(stats));
            },
            async getSalesSummary () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/business/sales-summary'
                });
                return SalesSummaryModel.fromDTO(res.data, getNow());
            },
            async getHistoricPaidExternallyInvoices () {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/historic-paid-externally-invoices"
                });
                return res.data.map(HistoricRevenueModel.fromHistoricInvoiceDTO);
            },
            async getFutureInvoiceTotals (param) {
                let { to } = param;
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/future-invoices/totals",
                    params: {
                        to
                    }
                });
                return res.data.map(HistoricRevenueModel.fromHistoricInvoiceDTO);
            },
            async getIssuedTotalAmount (vendorId) {
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: "/invoices/vendor/".concat(vendorId, "/issued-total-amount")
                });
                return res.data.totalAmount;
            }
        },
        connectivity: {
            async probeReachability () {
                await sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: '/status',
                    disableAuth: true,
                    disableRetry: true,
                    disableConnectivityTracking: true,
                    disableSentry: ()=>true,
                    disable403Handling: true,
                    config: {
                        timeout: PROBE_TIMEOUT_MS
                    }
                });
            }
        },
        featureFlag: {
            getAll (businessId, authToken) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/v2/feature-flags/fe',
                    params: {
                        businessId
                    },
                    authToken
                }).then(mapResponseBody);
            }
        },
        plaid: {
            getLinkToken (mode) {
                let { fundingSourceId, relationshipId } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/link-token',
                    data: {
                        mode,
                        fundingSourceId: fundingSourceId || null,
                        relationshipId
                    }
                }).then((response)=>response.data.token);
            },
            exchangePublicToken (body) {
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: '/exchange-public-token',
                    data: body,
                    disable403Handling: (err)=>new PayoutErrorResponse(err).authorizationError() != null,
                    disableSentry: (err)=>new PaymentMethodErrorResponse(err).isPlaidError() || new PayoutErrorResponse(err).authorizationError() != null
                }).then(mapResponseBody);
            },
            async verifyMicroDeposit (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/verify/".concat(fundingSourceId),
                    urlForSentry: '/verify/:fundingSourceId'
                });
            },
            async failedVerification (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/failed-verification/".concat(fundingSourceId),
                    urlForSentry: '/verify-failed/:fundingSourceId'
                });
            },
            isLoginRequired (fundingSourceId) {
                return sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/funding-source/".concat(fundingSourceId, "/is-login-required"),
                    urlForSentry: '/funging-source/:fundingSourceId/is-login-required'
                }).then((res)=>res.data.isLoginRequired);
            },
            async loginRepaired (fundingSourceId) {
                await sendRequest({
                    apiHost: APIHost.Plaid,
                    method: 'post',
                    url: "/funding-source/".concat(fundingSourceId, "/login-repaired"),
                    urlForSentry: '/funging-source/:fundingSourceId/login-repaired'
                });
            }
        },
        frontendLogging: {
            async log (report) {
                await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'post',
                    url: '/frontend-logging',
                    data: report,
                    disableSentry: ()=>true,
                    disable403Handling: true
                });
            }
        },
        proposalTemplates: {
            async getAll () {
                if (!selectors.user.isVendor(getState())) {
                    // Endpoint requires the Vendor role; non-vendors get 403.
                    return [];
                }
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/all",
                    disable403Handling: true
                }).then(mapResponseBody).then((templates)=>templates.map(ProposalTemplateSummaryModel.fromDTO));
            },
            isValidToSend (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(id, "/is-valid-to-send"),
                    urlForSentry: '/proposal-templates/:id/is-valid-to-send',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.ProposalTemplateDeleted,
                            ServerError.ProposalTemplateNotFound
                        ])
                }).then(mapResponseBody);
            },
            async get (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(id),
                    urlForSentry: '/proposal-templates/:id',
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.ProposalTemplateNotRelatedToVendor,
                            ServerError.ProposalTemplateNotFound
                        ])
                }).then(mapResponseBody).then(proposalTemplateModel.fromDTO);
            },
            async save (id, template) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id),
                    urlForSentry: '/proposal-templates/:id',
                    data: template,
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            createLegalTermsBuilderSession (templateId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(templateId, "/legal-terms/").concat(legalTermsId, "/builder-token"),
                    urlForSentry: '/proposal-templates/:templateId/legal-terms/:legalTermsId/builder-token',
                    disableSentry: (err)=>err.hasServerError(EXPECTED_SIGNATURE_INSTANCE_OPEN_ERRORS)
                }).then(mapResponseBody);
            },
            syncLegalTermsSignature (templateId, legalTermsId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(templateId, "/legal-terms/").concat(legalTermsId, "/signature/sync"),
                    urlForSentry: '/proposal-templates/:templateId/legal-terms/:legalTermsId/signature/sync',
                    disableSentry: (err)=>err.hasServerError(EXPECTED_SIGNATURE_INSTANCE_SYNC_ERRORS)
                }).then(mapResponseBody);
            },
            async rename (id, title) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/rename"),
                    urlForSentry: '/proposal-templates/:id/rename',
                    data: {
                        title
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            async duplicate (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/duplicate"),
                    urlForSentry: '/proposal-templates/:id/duplicate'
                }).then(mapResponseBody);
            },
            async removeV2 (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'delete',
                    url: "/v2/proposal-templates/".concat(id),
                    urlForSentry: '/v2/proposal-templates/:id'
                }).then(mapResponseBody);
            },
            async create (template) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/create",
                    urlForSentry: '/proposal-templates/create',
                    data: template,
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists)
                }).then(mapResponseBody);
            },
            async createDraft (id) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/".concat(id, "/create-relationship-draft"),
                    urlForSentry: '/proposal-templates/:id/create-relationship-draft'
                }).then(mapResponseBody);
            },
            async createFromDraft (id, title) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/proposal-templates/create-from-draft/".concat(id),
                    urlForSentry: '/proposal-templates/create-from-draft/:id',
                    disableSentry: (err)=>err.hasServerError(ServerError.NameAlreadyExists),
                    data: {
                        title
                    }
                }).then(mapResponseBody);
            },
            getBillingSchedule (templateId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(templateId, "/billing-schedule"),
                    urlForSentry: '/proposal-templates/:id/billing-schedule',
                    params
                }).then(mapResponseBody);
            },
            getBillingTimeline (templateId, params) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/proposal-templates/".concat(templateId, "/billing-timeline"),
                    urlForSentry: '/proposal-templates/:id/billing-timeline',
                    params
                }).then(mapResponseBody).then(toBillingTimeline);
            }
        },
        reports: {
            async revenueReportServicesV3 () {
                return sendRequest({
                    method: 'get',
                    url: "/revenue-report/services/v3",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async revenueReportCustomersV3 () {
                return sendRequest({
                    method: 'get',
                    url: "/revenue-report/customers/v3",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async revenueReportV3 (param) {
                let { start, end, breakdowns, aggregate, services, customers, sort } = param;
                const params = {
                    start,
                    end,
                    breakdowns,
                    aggregate,
                    sort
                };
                const queryParams = Object.entries(params).filter((param)=>{
                    let [, value] = param;
                    return !!value;
                }).map((param)=>{
                    let [key, value] = param;
                    return "".concat(key, "=").concat(value);
                }).join('&');
                return sendRequest({
                    method: 'post',
                    url: "/revenue-report/v3".concat(queryParams !== '' ? "?".concat(queryParams) : ''),
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined
                    }
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportServices () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/services",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportCustomers () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/customers",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportAgreements () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/agreements",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReportAssignees () {
                return sendRequest({
                    method: 'get',
                    url: "/active-services-report/assignees",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async activeServicesReport (param) {
                let { businessId, params, services, customers, agreements, assignees } = param;
                const queryParams = Object.entries(params).filter((param)=>{
                    let [, value] = param;
                    return value !== undefined;
                }).map((param)=>{
                    let [key, value] = param;
                    return Array.isArray(value) ? "".concat(key, "=").concat(value.join(',')) : "".concat(key, "=").concat(value);
                }).join('&');
                return sendRequest({
                    method: 'post',
                    url: "/businesses/".concat(businessId, "/active-services-report").concat(queryParams !== '' ? "?".concat(queryParams) : ''),
                    urlForSentry: '/businesses/:businessId/active-services-report',
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined,
                        agreements: agreements.length > 0 ? agreements : undefined,
                        assignees: assignees.length > 0 ? assignees : undefined
                    }
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportServices () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/services",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportCustomers () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/customers",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReportAgreements () {
                return sendRequest({
                    method: 'get',
                    url: "/remaining-credit-report/agreements",
                    apiHost: APIHost.Scout
                }).then(mapResponseBody).catch(()=>[]);
            },
            async remainingCreditReport (param) {
                let { services, customers, agreements, sort } = param;
                return sendRequest({
                    method: 'post',
                    url: "/remaining-credit-report",
                    apiHost: APIHost.Scout,
                    data: {
                        services: services.length > 0 ? services : undefined,
                        customers: customers.length > 0 ? customers : undefined,
                        agreements: agreements.length > 0 ? customers : undefined,
                        sort
                    }
                }).then(mapResponseBody).then((entries)=>entries.map(RemainingCreditReportEntryModel.fromDTO)).catch(()=>[]);
            },
            async getFutureInvoicesReport (params) {
                const serializeArray = (array)=>array && array.length > 0 ? array.join(',') : undefined;
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices',
                    urlForSentry: '/future-invoices',
                    apiHost: APIHost.Scout,
                    params: {
                        companyName: serializeArray(params.companies),
                        agreementName: serializeArray(params.agreements),
                        requiresApproval: serializeArray(params.requiresApproval),
                        issueDateFrom: params.issueDateFrom,
                        issueDateTo: params.issueDateTo,
                        dueDateFrom: params.dueDateFrom,
                        dueDateTo: params.dueDateTo,
                        sortField: params.sortField,
                        sortDirection: params.sortDirection
                    }
                }).then((res)=>res.data.map(FutureInvoiceModel.fromDTO));
            },
            async getFutureInvoicesByCustomer (customerId) {
                return sendRequest({
                    method: 'get',
                    apiHost: APIHost.Scout,
                    url: "/future-invoices/customer/".concat(customerId),
                    urlForSentry: '/future-invoices/customer/:customerId'
                }).then((res)=>res.data.map(FutureInvoiceModel.fromDTO));
            },
            async getFutureInvoicesCompanyNames () {
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices/company-names',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            },
            async getFutureInvoicesAgreementNames () {
                return sendRequest({
                    method: 'get',
                    url: '/future-invoices/agreement-names',
                    apiHost: APIHost.Scout
                }).then(mapResponseBody);
            }
        },
        karbon: {
            async setAPIKey (apiKey) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon',
                    urlForSentry: '/integrations/karbon',
                    data: {
                        type: 'apiKey',
                        apiKey: {
                            key: apiKey
                        }
                    }
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon/disconnect',
                    urlForSentry: '/integrations/karbon/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/karbon/items',
                    urlForSentry: '/integrations/karbon/items',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/karbon/items/link',
                    urlForSentry: '/integrations/karbon/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/karbon/".concat(serviceTemplateId, "/roles"),
                    disableSentry: isIntegrationNotConnected,
                    urlForSentry: "/integrations/karbon/serviceTemplateId/roles"
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/karbon/users',
                    urlForSentry: '/integrations/karbon/users',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            },
            async updateKarbonIntegration (defaultAssigneeId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/karbon/update",
                    urlForSentry: '/integrations/karbon/update',
                    data: {
                        source: 'karbon',
                        karbon: {
                            defaultAssigneeId
                        }
                    }
                });
            },
            async getIntegration () {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/karbon",
                    urlForSentry: '/integrations/karbon'
                }).then(mapResponseBody);
            }
        },
        clientHub: {
            async setAPIKey (apiKey) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub',
                    urlForSentry: '/integrations/client-hub',
                    data: {
                        type: 'apiKey',
                        apiKey: {
                            key: apiKey
                        }
                    }
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub/disconnect',
                    urlForSentry: '/integrations/client-hub/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/client-hub/items',
                    urlForSentry: '/integrations/client-hub/items',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/client-hub/items/link',
                    urlForSentry: '/integrations/client-hub/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            },
            async getRoles (serviceTemplateId) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/integrations/client-hub/".concat(serviceTemplateId, "/roles"),
                    disableSentry: isIntegrationNotConnected,
                    urlForSentry: "/integrations/client-hub/serviceTemplateId/roles"
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.roles || [] : [];
            },
            async getUsers () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/client-hub/users',
                    urlForSentry: '/integrations/client-hub/users',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.users || [] : [];
            }
        },
        keeper: {
            async connect (param) {
                let { clientId, clientSecret } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper',
                    data: {
                        type: 'accessToken',
                        accessToken: {
                            clientId,
                            clientSecret
                        }
                    },
                    disableSentry: (err)=>err.hasServerError(ServerError.InvalidKeeperCredentials)
                });
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper/disconnect'
                });
            },
            async getItems () {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: '/integrations/keeper/items',
                    disableSentry: isIntegrationNotConnected
                });
                return res.data.status === IntegrationResponseStatus.Success ? res.data.items || [] : [];
            },
            async linkServiceTemplate (id, integratedIds) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/keeper/items/link',
                    data: {
                        id,
                        integratedIds
                    }
                });
            }
        },
        stanfordTax: {
            async connect (data) {
                const res = await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/stanford-tax',
                    data,
                    disableSentry: (err)=>err.hasServerError([
                            ServerError.StanfordTaxFirmIdentifierMalformed,
                            ServerError.StanfordTaxFirmNotFound,
                            ServerError.StanfordTaxFirmNotActive
                        ])
                });
                return res.data;
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/stanford-tax/disconnect'
                });
            },
            async linkServiceTemplate (param) {
                let { serviceTemplateId, data } = param;
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/integrations/stanford-tax/service-templates/".concat(serviceTemplateId, "/link"),
                    urlForSentry: '/integrations/stanford-tax/service-templates/:id/link',
                    data
                });
            }
        },
        xero: {
            async connect (param) {
                let { redirectUrl } = param;
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/accounting/xero/connect',
                    data: {
                        redirectUrl
                    }
                }).then(mapResponseBody);
            },
            async disconnect () {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/integrations/accounting/xero/disconnect'
                }).then(mapResponseBody);
            }
        },
        slack: {
            async sendCsNotification (text) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/slack/cs-notification',
                    data: {
                        text
                    },
                    disableSentry: ()=>true
                });
            },
            async sendPayoutValidation (text) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/slack/payout-validation',
                    data: {
                        text
                    },
                    disableSentry: ()=>true
                });
            },
            async sendFeatureFeedback (text) {
                await sendRequest({
                    method: 'post',
                    apiHost: APIHost.Scout,
                    url: '/slack/feature-feedback',
                    data: {
                        text
                    }
                });
            }
        },
        agreementImport: {
            async extract (data) {
                const response = await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agreement-import/v2/extract',
                    data,
                    disableSentry: (err)=>err.hasStatus([
                            422,
                            413
                        ])
                }).then(mapResponseBody).catch((err)=>{
                    var _err_response;
                    const body = (_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.data;
                    if ((body === null || body === void 0 ? void 0 : body.reason) != null) {
                        return {
                            reason: body.reason,
                            conversationId: body.conversationId
                        };
                    }
                    throw err;
                });
                if ('reason' in response) {
                    return {
                        success: false,
                        reason: response.reason,
                        conversationId: response.conversationId
                    };
                }
                const { conversationId } = response, extractResult = _object_without_properties(response, [
                    "conversationId"
                ]);
                return {
                    success: true,
                    conversationId,
                    extraction: AgreementImportExtractionModel.fromDTO(extractResult)
                };
            },
            async extractOld (data) {
                const response = await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agreement-import/extract',
                    urlForSentry: '/api/agreement-import/extract',
                    data,
                    disableSentry: (err)=>err.hasStatus([
                            422,
                            413
                        ])
                }).then(mapResponseBody).catch((err)=>{
                    var _err_response;
                    const body = (_err_response = err.response) === null || _err_response === void 0 ? void 0 : _err_response.data;
                    if ((body === null || body === void 0 ? void 0 : body.reason) != null) {
                        return {
                            reason: body.reason,
                            conversationId: body.conversationId
                        };
                    }
                    throw err;
                });
                if ('reason' in response) {
                    return {
                        success: false,
                        reason: response.reason,
                        conversationId: response.conversationId
                    };
                }
                const { conversationId } = response, extractResult = _object_without_properties(response, [
                    "conversationId"
                ]);
                return {
                    success: true,
                    conversationId,
                    extraction: AgreementImportExtractionModel.fromDTO(extractResult)
                };
            },
            async feedback (data) {
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agreement-import/".concat(data.conversationId, "/feedback"),
                    urlForSentry: '/api/agreement-import/:conversationId/feedback',
                    data: {
                        rating: data.rating
                    }
                });
            },
            async create (data) {
                const dto = data.extraction.toDTO();
                const body = {
                    agreementName: dto.agreementName,
                    effectiveDate: dto.effectiveDate,
                    netTerms: dto.netTerms,
                    hasClientSignature: dto.hasClientSignature,
                    client: dto.client,
                    services: dto.services,
                    legalTerms: dto.legalTerms
                };
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agreement-import/".concat(data.conversationId, "/create"),
                    urlForSentry: '/api/agreement-import/:conversationId/create',
                    data: body
                }).then(mapResponseBody);
            }
        },
        agenticDraft: {
            create: async ()=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/draft',
                    data: {}
                }).then(mapResponseBody).then(toCreatedConversation);
            },
            createForDraft: async (draftId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(draftId),
                    urlForSentry: '/api/agent/draft/:draftId',
                    data: {}
                }).then(mapResponseBody).then(toCreatedConversation);
            },
            findByDraft: async (draftId)=>{
                try {
                    const body = await sendRequest({
                        apiHost: APIHost.AI,
                        method: 'get',
                        url: "/api/agent/draft/by-draft/".concat(draftId),
                        urlForSentry: '/api/agent/draft/by-draft/:draftId',
                        disableSentry: (e)=>e.status === 404
                    }).then(mapResponseBody);
                    return ConversationModel.fromDTO(body);
                } catch (err) {
                    const wrapped = new ErrorResponse(err);
                    if (wrapped.status === 404) return null;
                    throw err;
                }
            },
            attachDraft: async (conversationId, draftId)=>{
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/attach-draft"),
                    urlForSentry: '/api/agent/draft/:conversationId/attach-draft',
                    data: {
                        draftId
                    }
                });
            },
            get: async (conversationId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/draft/".concat(conversationId),
                    urlForSentry: '/api/agent/draft/:conversationId'
                }).then(mapResponseBody).then(ConversationModel.fromDTO);
            },
            feedback: async (param)=>{
                let { conversationId, messageId, rating, comment } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/feedback"),
                    urlForSentry: '/api/agent/draft/:conversationId/feedback',
                    data: {
                        messageId,
                        rating,
                        comment
                    }
                });
            },
            dismissFeedback: async (param)=>{
                let { conversationId, messageId } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/draft/".concat(conversationId, "/feedback/dismiss"),
                    urlForSentry: '/api/agent/draft/:conversationId/feedback/dismiss',
                    data: {
                        messageId
                    }
                });
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/draft/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/draft/:conversationId/messages',
                    data: args.message.toWirePayload(),
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            },
            undo: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/draft/".concat(args.conversationId, "/undo"),
                    urlForSentry: '/api/agent/draft/:conversationId/undo',
                    data: {
                        messageId: args.messageId
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            }
        },
        agenticBulkDraft: {
            create: async (params)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/bulk-draft',
                    data: params != null ? {
                        scope: params
                    } : {}
                }).then(mapResponseBody).then(toCreatedConversation);
            },
            get: async (conversationId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/bulk-draft/".concat(conversationId),
                    urlForSentry: '/api/agent/bulk-draft/:conversationId'
                }).then(mapResponseBody).then(ConversationModel.fromDTO);
            },
            recent: async ()=>{
                try {
                    var _body_scope;
                    const body = await sendRequest({
                        apiHost: APIHost.AI,
                        method: 'get',
                        url: '/api/agent/bulk-draft/recent',
                        disableSentry: (e)=>e.status === 404
                    }).then(mapResponseBody);
                    return {
                        conversation: ConversationModel.fromDTO(body),
                        scope: (_body_scope = body.scope) !== null && _body_scope !== void 0 ? _body_scope : null
                    };
                } catch (err) {
                    const wrapped = new ErrorResponse(err);
                    if (wrapped.status === 404) return null;
                    throw err;
                }
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/bulk-draft/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/bulk-draft/:conversationId/messages',
                    data: _object_spread_props(_object_spread({}, args.message.toWirePayload()), {
                        scope: args.scope
                    }),
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            },
            undo: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/bulk-draft/".concat(args.conversationId, "/undo"),
                    urlForSentry: '/api/agent/bulk-draft/:conversationId/undo',
                    data: {
                        messageId: args.messageId
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            },
            feedback: async (param)=>{
                let { conversationId, messageId, rating, comment } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/bulk-draft/".concat(conversationId, "/feedback"),
                    urlForSentry: '/api/agent/bulk-draft/:conversationId/feedback',
                    data: {
                        messageId,
                        rating,
                        comment
                    }
                });
            }
        },
        agenticBulkAmendment: {
            create: async (params)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/bulk-amendment',
                    data: params != null ? {
                        scope: params
                    } : {}
                }).then(mapResponseBody).then(toCreatedConversation);
            },
            get: async (conversationId)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/bulk-amendment/".concat(conversationId),
                    urlForSentry: '/api/agent/bulk-amendment/:conversationId'
                }).then(mapResponseBody).then(toAmendmentConversation);
            },
            recent: async ()=>{
                try {
                    var _body_scope;
                    const body = await sendRequest({
                        apiHost: APIHost.AI,
                        method: 'get',
                        url: '/api/agent/bulk-amendment/recent',
                        disableSentry: (e)=>e.status === 404
                    }).then(mapResponseBody);
                    return {
                        conversation: toAmendmentConversation(body),
                        scope: (_body_scope = body.scope) !== null && _body_scope !== void 0 ? _body_scope : null
                    };
                } catch (err) {
                    const wrapped = new ErrorResponse(err);
                    if (wrapped.status === 404) return null;
                    throw err;
                }
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/bulk-amendment/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/bulk-amendment/:conversationId/messages',
                    data: _object_spread_props(_object_spread({}, args.message.toWirePayload()), {
                        scope: args.scope
                    }),
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            },
            undo: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/bulk-amendment/".concat(args.conversationId, "/undo"),
                    urlForSentry: '/api/agent/bulk-amendment/:conversationId/undo',
                    data: {
                        messageId: args.messageId
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAgentStreamParser(),
                    signal: args.signal,
                    disableSentry: isRefusedSellerGesture
                });
            },
            feedback: async (param)=>{
                let { conversationId, messageId, rating, comment } = param;
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/bulk-amendment/".concat(conversationId, "/feedback"),
                    urlForSentry: '/api/agent/bulk-amendment/:conversationId/feedback',
                    data: {
                        messageId,
                        rating,
                        comment
                    }
                });
            }
        },
        draftsAiSearch: {
            invoke: async (args)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/drafts-search',
                    data: {
                        query: args.query
                    },
                    config: {
                        signal: args.signal,
                        timeout: args.timeoutMs
                    },
                    disableConnectivityTracking: true
                }).then(mapResponseBody);
            }
        },
        agreementsAiSearch: {
            invoke: async (args)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/agreements-search',
                    data: {
                        query: args.query
                    },
                    config: {
                        signal: args.signal,
                        timeout: args.timeoutMs
                    },
                    disableConnectivityTracking: true
                }).then(mapResponseBody);
            }
        },
        analyticAgent: {
            create: async ()=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: '/api/agent/analyst',
                    data: {}
                }).then(mapResponseBody);
            },
            get: async (id)=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/agent/analyst/".concat(id),
                    urlForSentry: '/api/agent/analyst/:id'
                }).then(mapResponseBody);
            },
            feedback: async (args)=>{
                await sendRequest({
                    apiHost: APIHost.AI,
                    method: 'post',
                    url: "/api/agent/analyst/".concat(args.conversationId, "/feedback"),
                    urlForSentry: '/api/agent/analyst/:conversationId/feedback',
                    data: {
                        messageId: args.messageId,
                        rating: args.rating,
                        comment: args.comment
                    }
                });
            },
            sendMessage: async (args)=>{
                return sendStream({
                    apiHost: APIHost.AI,
                    url: "/api/agent/analyst/".concat(args.conversationId, "/messages"),
                    urlForSentry: '/api/agent/analyst/:conversationId/messages',
                    data: {
                        text: args.content
                    },
                    onEvent: args.onEvent,
                    parseEvent: createAnalystStreamParser(),
                    signal: args.signal
                });
            }
        },
        pulse: {
            getSummary: async ()=>{
                return sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'get',
                    url: '/revenue-opportunities/summary'
                }).then(mapResponseBody);
            },
            createDismissal: async (data)=>{
                const res = await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'post',
                    url: '/revenue-opportunities/dismissals',
                    data
                });
                return {
                    id: res.data.id,
                    created: res.status === 201
                };
            },
            deleteDismissal: async (dismissalId)=>{
                await sendRequest({
                    apiHost: APIHost.Scout,
                    method: 'delete',
                    url: "/revenue-opportunities/dismissals/".concat(encodeURIComponent(dismissalId)),
                    urlForSentry: '/revenue-opportunities/dismissals/:dismissalId'
                });
            }
        },
        arvBenchmark: {
            get: async ()=>{
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: '/api/agent/arv-benchmark'
                }).then(mapResponseBody);
            }
        },
        customPageTemplates: {
            async getAll (businessId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page-template"),
                    urlForSentry: '/business/:businessId/custom-page-template'
                }).then(mapResponseBody);
            },
            async getById (businessId, templateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId'
                }).then(mapResponseBody);
            },
            async create (businessId, data) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/create"),
                    urlForSentry: '/business/:businessId/custom-page-template/create',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.CustomPageTemplateDuplicateName)
                }).then(mapResponseBody);
            },
            async update (businessId, templateId, data) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/update"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/update',
                    data,
                    disableSentry: (err)=>err.hasServerError(ServerError.CustomPageTemplateDuplicateName)
                });
            },
            async duplicate (businessId, templateId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/duplicate"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/duplicate'
                }).then(mapResponseBody);
            },
            async delete (businessId, templateId) {
                await sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page-template/").concat(templateId, "/delete"),
                    urlForSentry: '/business/:businessId/custom-page-template/:templateId/delete'
                });
            }
        },
        customPage: {
            async createText (businessId, sourceId, content) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page/text"),
                    urlForSentry: '/business/:businessId/custom-page/text',
                    data: {
                        sourceId,
                        content
                    }
                }).then(mapResponseBody);
            },
            async updateText (businessId, textId, sourceId, content) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: "/business/".concat(businessId, "/custom-page/text/").concat(textId, "/update"),
                    urlForSentry: '/business/:businessId/custom-page/text/:textId/update',
                    data: {
                        sourceId,
                        content
                    }
                }).then(mapResponseBody);
            },
            async getText (sourceId, textId) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/texts/".concat(sourceId, "/").concat(textId),
                    urlForSentry: '/texts/:sourceId/:id'
                }).then(mapResponseBody);
            },
            async getDownloadUrl (businessId, pdfName) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'get',
                    url: "/business/".concat(businessId, "/custom-page/").concat(encodeURIComponent(pdfName), "/download"),
                    urlForSentry: '/business/:businessId/custom-page/:pdfName/download'
                }).then(mapResponseBody);
            }
        },
        branding: {
            extractFromWebsite (websiteUrl) {
                return sendRequest({
                    apiHost: APIHost.AI,
                    method: 'get',
                    url: "/api/branding/extract?url=".concat(encodeURIComponent(websiteUrl)),
                    urlForSentry: '/api/branding/extract',
                    disableSentry: (err)=>err.hasStatus([
                            422
                        ])
                }).then(mapResponseBody);
            }
        },
        oauth: {
            issueCode (payload) {
                return sendRequest({
                    apiHost: APIHost.Mgmt,
                    method: 'post',
                    url: '/internal/oauth/issue-code',
                    data: payload
                }).then(mapResponseBody);
            }
        },
        pdf: {
            getInvoicePdfUrl (invoiceId) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: "/invoice-pdfs/".concat(invoiceId, "/url"),
                    urlForSentry: '/invoice-pdfs/:invoiceId/url'
                }).then((res)=>res.data.url);
            },
            generateRelationshipPdf (relationshipId, lang, includeAmendmentsLog) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: "/generate-relationship-pdf/".concat(relationshipId),
                    urlForSentry: '/generate-relationship-pdf/:relationshipId',
                    params: {
                        lang,
                        includeAmendmentsLog
                    },
                    data: {}
                }).then(mapResponseBody);
            }
        },
        jobs: {
            getJobs () {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'get',
                    url: '/jobs'
                }).then(mapResponseBody);
            },
            cancelJob (jobId) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'delete',
                    url: "/jobs/".concat(jobId),
                    urlForSentry: '/jobs/:jobId'
                }).then(mapResponseBody);
            },
            markJobsSeen (entries) {
                return sendRequest({
                    apiHost: APIHost.External,
                    method: 'post',
                    url: '/jobs',
                    data: entries
                }).then(mapResponseBody);
            }
        }
    };
}
